import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Edit3,
  Trash2,
  Calendar,
  FileText,
  TrendingUp,
  TrendingDown,
  Tag,
  AlertTriangle,
  Save,
  X,
  Clock,
  Sparkles,
  Activity,
  Copy,
  Check,
  Eye,
  EyeOff,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  PinOff,
  Wand2,
  Pause,
  Play,
  Search,
  Info
} from 'lucide-react';
import { getTradeNetPnL } from '../config';
import { getSpec } from '../config/futuresSpecs';
import { getCurrentTradingDay } from '../services/tradeProcessor';
import { getExecutionInstantMs } from '../services/timeService';
import { findSessionById } from '../services/sessionUtils';
import { EconomicEventsPanel, EVENT_TAGS } from './EconomicCalendar';
import RichTextEditor from './UI/RichTextEditor';
import { fmtUSD } from '../utils/formatters';
import { SESSION_COLORS as SHARED_SESSION_COLORS } from '../utils/constants';
import { stripAutoSyncedPrefix } from '../utils/notesHtml';
import RichTextDisplay from './UI/RichTextDisplay';
import GlassSelect from './Common/GlassSelect';
import { coach, SEVERITY_STYLE, buildSessionSignature, filterMuted, enrichWithMuteAction } from '../services/coach';
import LiveCoachFeed from './Coach/LiveCoachFeed';
import TradeFlagChips from './Coach/TradeFlagChips';
import CounterSignalChips from './Coach/CounterSignalChips';
import ObservationActions from './Coach/ObservationActions';
import PlanGeneratorButton from './Coach/PlanGeneratorButton';
import LiveTradePanel from './LiveJournal/LiveTradePanel';
import {
  autoTagTradesFromEvents,
  tagTradesWithEvent,
  diffTagApplications,
  DEFAULT_PROXIMITY_MIN
} from '../utils/eventAutoTag';


// Module-level frozen empty defaults — hoisted out of the component so that
// using them as fallbacks ({...} || EMPTY_X) doesn't churn child memos with a
// fresh object/array reference on every render.
const EMPTY_JOURNAL_ENTRY = Object.freeze({ plan: '', news: '', notes: '', eventTags: [] });
const EMPTY_NEWS_EVENTS = Object.freeze([]);

const JournalView = ({
  darkMode,
  trades,
  journalEntries,
  setJournalEntries,
  selectedAccount,
  accounts,
  onEditTrade,
  onDeleteTrade,
  onAddTrade,
  getAccountById,
  futuresSpecs,
  allTrades,
  setTrades,
  onShowDailyReview,
  eventTags = [],  // Use passed event tags from Settings, fallback to empty array
  economicCalendarSettings = {},  // Settings for economic events filtering
  selectedDate: selectedDateProp,  // Optional: controlled date from parent
  setSelectedDate: setSelectedDateProp,  // Optional: controlled date setter from parent
  allConfluences = [],  // All confluences (default + custom) for name lookup
  mistakes = [],  // All mistakes (default + custom) for bulk-apply menu
  currentTradingDay,  // Current futures trading day (auto-updates at 6pm ET boundary)
  sessions = [],  // Trade session groups (campaigns/thesis)
  setSessions,  // Setter for sessions state
  playbooks = [],  // Playbook library — strategic intent patterns (Rejection, Breakout, etc.)
  setPlaybooks,  // Setter for playbook library
  userTier = 'free',  // 'free' | 'pro' — derived from redeemed license; gates advanced Coach analyzers
  onOpenLicenseSettings,  // () => void — opens Settings modal on the License tab (used by LiveCoachFeed upsell)
  onOpenCoachDashboard,   // () => void — opens the persistent Coach Dashboard (Phase 2 mental model)
  onOpenWeeklyDigest,     // () => void — opens the Weekly Coach Digest modal
  onOpenWhatIfBacktest,   // () => void — opens the What-If Backtest modal (Phase 14)
  coachHealth = null,     // pre-computed Coach Health Score (Phase 14) for the LiveCoachFeed pill
  coachAlerts = null,     // Real-Time Alerts settings slice (Phase 15) for LiveTradePanel auto-dispatch
  // Phase 15 — Plan Generator inputs. When all are present + LLM ready,
  // a "Generate" button appears in the Plan section header.
  coachProfile = null,    // current CoachProfile from useCoachProfile (for plan context)
  coachLLMSettings = null,// {apiKey, model, voice, ttsEnabled, enabled}
  // Phase 17 — NinjaTrader ATM template version history keyed by ATM name.
  // Threaded into LiveCoachFeed so the day-scope atmParamDirection analyzer
  // can scan cross-ATM param-change patterns. Optional — undefined silently
  // disables that analyzer.
  ntTemplateVersions = {},
  // Setup library with rules + recommended confluences. Threaded into the
  // session-scope coach.analyze ctx so confluenceConsensus can filter out
  // the "Pin to X" button once the confluence is already pinned (fixes the
  // bug where the button briefly flashed "Pinned ✓" then reverted to the
  // original label because the analyzer kept emitting the action).
  setupDetails = {},
  onCoachAction,  // (action, observation) => Promise|void — dispatcher for structured Coach actions
  mutedObservations = {}  // Map of observationId → ISO expiresAt — for filtering + Mute action UX
}) => {
  // Sidebar collapse/pin state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(true); // pinned = always visible when expanded
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const sidebarOpen = sidebarPinned || !sidebarCollapsed || sidebarHovered;

  // Delay-based hover behavior:
  //   Open delay prevents the sidebar from popping out when the cursor swipes across
  //   it on the way back to the charts. Close delay prevents flickering when the
  //   cursor briefly leaves and ensures reliable retraction.
  const sidebarHoverOpenTimerRef = useRef(null);
  const sidebarHoverCloseTimerRef = useRef(null);
  const SIDEBAR_HOVER_OPEN_DELAY_MS = 300;
  const SIDEBAR_HOVER_CLOSE_DELAY_MS = 1000;
  const cancelSidebarOpen = () => {
    if (sidebarHoverOpenTimerRef.current) {
      clearTimeout(sidebarHoverOpenTimerRef.current);
      sidebarHoverOpenTimerRef.current = null;
    }
  };
  const cancelSidebarClose = () => {
    if (sidebarHoverCloseTimerRef.current) {
      clearTimeout(sidebarHoverCloseTimerRef.current);
      sidebarHoverCloseTimerRef.current = null;
    }
  };
  const scheduleSidebarOpen = () => {
    cancelSidebarClose();
    cancelSidebarOpen();
    sidebarHoverOpenTimerRef.current = setTimeout(() => {
      setSidebarHovered(true);
      sidebarHoverOpenTimerRef.current = null;
    }, SIDEBAR_HOVER_OPEN_DELAY_MS);
  };
  const scheduleSidebarClose = () => {
    cancelSidebarOpen();
    cancelSidebarClose();
    sidebarHoverCloseTimerRef.current = setTimeout(() => {
      setSidebarHovered(false);
      sidebarHoverCloseTimerRef.current = null;
    }, SIDEBAR_HOVER_CLOSE_DELAY_MS);
  };
  // Cleanup timers on unmount
  useEffect(() => () => { cancelSidebarOpen(); cancelSidebarClose(); }, []);

  // Confluence copy mode state
  const [confluenceCopySource, setConfluenceCopySource] = useState(null); // trade object being copied from
  const [confluenceCopySelected, setConfluenceCopySelected] = useState(new Set()); // selected trade IDs for copy

  // Account number visibility toggle
  const [hideAccounts, setHideAccounts] = useState(false);

  // Session grouping mode state
  const [sessionGroupMode, setSessionGroupMode] = useState(false);
  const [sessionGroupSelected, setSessionGroupSelected] = useState(new Set());
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionColor, setNewSessionColor] = useState('blue');
  const [newSessionThesis, setNewSessionThesis] = useState('');
  // Pending add-to-existing flow: user picks a session from dropdown -> trades get auto-checked -> confirm button appears
  const [pendingAddSessionId, setPendingAddSessionId] = useState(null);
  // Inline rename of a session banner: { id, name }
  const [renamingSession, setRenamingSession] = useState(null);
  // Collapsed session IDs (Set) — banner shows summary, trade cards hidden
  const [collapsedSessions, setCollapsedSessions] = useState(new Set());
  // Bulk-apply popover: which session ID is showing the menu, plus active tab
  const [bulkApplyMenu, setBulkApplyMenu] = useState(null); // { sessionId, tab: 'confluences'|'tags'|'mistakes' } or null
  // Bulk-apply popover filters (v7.5.9 navigation upgrade) — search text +
  // status filter for the chip grid. Reset to defaults whenever the popover
  // closes (see the click-to-close handler that nulls bulkApplyMenu).
  //
  // Categories default to ALL COLLAPSED so the popover opens compact —
  // user picks a category to drill into. Tracking the EXPANDED set instead
  // of the COLLAPSED set inverts the default for free (empty set = nothing
  // expanded = everything collapsed).
  const [bulkApplySearch, setBulkApplySearch] = useState('');
  const [bulkApplyFilter, setBulkApplyFilter] = useState('all'); // 'all'|'used'|'consensus'|'unused'
  const [bulkApplyExpanded, setBulkApplyExpanded] = useState(new Set()); // category names that are user-expanded
  // Inline thesis editor: { id, thesis }
  const [editingThesis, setEditingThesis] = useState(null);
  // Color picker popover: which session ID is showing it
  const [colorPickerSessionId, setColorPickerSessionId] = useState(null);
  // Inline lessons editor: { id, lessons }
  const [editingLessons, setEditingLessons] = useState(null);
  // Per-session AI insight loading state (Set of session IDs currently fetching)
  const [aiInsightLoading, setAiInsightLoading] = useState(new Set());
  // Playbook picker popover: which session ID has it open (or null)
  const [playbookPickerSessionId, setPlaybookPickerSessionId] = useState(null);
  // Draft name typed in the "create new playbook" inline input
  const [newPlaybookDraft, setNewPlaybookDraft] = useState('');
  // Session banner highlight state — set briefly when the Live Coach Feed
  // jumps to a session. A purple ring pulses for ~1.8s then clears. We keep
  // the timer id in a ref so quick successive jumps don't fight each other.
  const [highlightedSessionId, setHighlightedSessionId] = useState(null);
  const highlightTimerRef = useRef(null);

  /**
   * Scroll to the session banner with `sessionId` and briefly highlight it.
   * Called by LiveCoachFeed when a TODAY item is clicked. Uses the stable
   * DOM id `session-banner-${sessionId}` attached to the banner wrapper.
   */
  const jumpToSession = React.useCallback((sessionId) => {
    if (!sessionId) {
      console.warn('[Journal] jumpToSession called without sessionId — no-op');
      return;
    }
    const el = document.getElementById(`session-banner-${sessionId}`);
    if (!el) {
      // Banner not in DOM — usually means the session belongs to a different
      // trading day than the one currently rendered. The Coach panel can fire
      // this when the user clicks a session card before navigating to that day.
      console.warn(`[Journal] jumpToSession: no banner found for session ${sessionId} (likely on a different day)`);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedSessionId(sessionId);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedSessionId(null);
      highlightTimerRef.current = null;
    }, 1800);
  }, []);

  // Clear any pending highlight timer on unmount
  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  // ── AI Coach height-matching ─────────────────────────────────────────────
  // Lock the Live Coach Feed's max-height to the left column's natural height
  // (at lg+ breakpoints) so the two columns stay the same length. Prevents the
  // grid row from stretching to a tall Coach and leaving dead space under the
  // shorter News/Plan/EOD stack. Below lg the panel stacks full-width and we
  // don't constrain it.
  const leftColumnRef = useRef(null);
  const [leftColumnHeight, setLeftColumnHeight] = useState(null);
  useEffect(() => {
    const el = leftColumnRef.current;
    if (!el || typeof ResizeObserver === 'undefined' || typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const measure = () => {
      if (mq.matches) {
        setLeftColumnHeight(el.getBoundingClientRect().height);
      } else {
        setLeftColumnHeight(null);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // matchMedia listeners: use modern .addEventListener where available
    if (mq.addEventListener) {
      mq.addEventListener('change', measure);
    } else if (mq.addListener) {
      mq.addListener(measure);
    }
    return () => {
      ro.disconnect();
      if (mq.removeEventListener) {
        mq.removeEventListener('change', measure);
      } else if (mq.removeListener) {
        mq.removeListener(measure);
      }
    };
  }, []);

  const toggleSessionCollapsed = (sessionId) => {
    setCollapsedSessions(prev => {
      const next = new Set(prev);
      next.has(sessionId) ? next.delete(sessionId) : next.add(sessionId);
      return next;
    });
  };

  // Apply (toggle) a single confluence/tag/mistake/counterConfluence across all
  // trades in a session.
  //
  // `mutexField` (optional) enforces mutual exclusion with another array field.
  // When the user adds a value to `field`, the same value is stripped from
  // `mutexField` on the same trade. Used so a chip can't be both a positive
  // confluence AND a counter-confluence on the same trade — the same mutex
  // semantics the Edit Trade Modal applies per-trade.
  const bulkApplyToSession = (sessionId, field, value, mutexField = null) => {
    setTrades(prev => {
      const sessionTradeIds = prev.filter(t => t.sessionId === sessionId).map(t => t.id);
      // Determine if every trade already has this value -> toggle off; else add to all that don't have it
      const allHave = sessionTradeIds.every(id => {
        const t = prev.find(x => x.id === id);
        return Array.isArray(t[field]) && t[field].includes(value);
      });
      return prev.map(t => {
        if (t.sessionId !== sessionId) return t;
        const arr = Array.isArray(t[field]) ? t[field] : [];
        if (allHave) {
          // Toggling OFF — never touch the mutex field. The user wants the
          // value gone from `field`; their state on `mutexField` (if any) is
          // independent.
          return { ...t, [field]: arr.filter(v => v !== value) };
        }
        // Toggling ON — add to `field`, strip from `mutexField` if present.
        const withValue = arr.includes(value) ? t : { ...t, [field]: [...arr, value] };
        if (!mutexField) return withValue;
        const mutexArr = Array.isArray(withValue[mutexField]) ? withValue[mutexField] : [];
        if (!mutexArr.includes(value)) return withValue;
        return { ...withValue, [mutexField]: mutexArr.filter(v => v !== value) };
      });
    });
  };

  // Session color palette
  const SESSION_COLORS = SHARED_SESSION_COLORS;

  const getSessionById = (id) => sessions.find(s => s.id === id);
  const getSessionColor = (session) => SESSION_COLORS[session?.color] || SESSION_COLORS.blue;

  // -----------------------------------------------------------------------
  // Playbook helpers — strategic intent patterns reusable across sessions
  // -----------------------------------------------------------------------
  // Playbook is a higher-level classification than Setup. Setups answer
  // "how did I enter this trade"; Playbooks answer "what was my strategic
  // intent for this group." Optional per-session — unassigned sessions
  // still work fine; Playbooks just unlock cross-session aggregation.
  // Reuse the shared session palette so Playbook chips look at home next to
  // session dots. Keys map into SESSION_COLORS above.
  const PLAYBOOK_COLORS = ['purple', 'cyan', 'amber', 'rose', 'green', 'blue'];
  const getPlaybookColor = (playbook) => SESSION_COLORS[playbook?.color] || SESSION_COLORS.purple;

  const getPlaybookById = (id) => (playbooks || []).find(p => p.id === id);

  // Create a new playbook in the library. Returns the new playbook object
  // so callers can assign it to a session in the same interaction.
  const createPlaybook = (name, description = '') => {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    // Dedupe by name (case-insensitive) — reuse existing if one matches
    const existing = (playbooks || []).find(
      p => (p.name || '').trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) return existing;
    const nextColor = PLAYBOOK_COLORS[(playbooks || []).length % PLAYBOOK_COLORS.length];
    const playbook = {
      id: `pb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: trimmed,
      description: (description || '').trim(),
      color: nextColor,
      createdAt: new Date().toISOString()
    };
    if (typeof setPlaybooks === 'function') {
      setPlaybooks(prev => [...(prev || []), playbook]);
    }
    return playbook;
  };

  // Assign (or clear) a playbook on a session. Pass null to unassign.
  const assignPlaybookToSession = (sessionId, playbookId) => {
    setSessions(prev => prev.map(s =>
      s.id === sessionId ? { ...s, playbookId: playbookId || null } : s
    ));
  };

  // Create a new session from selected trades
  const createSession = () => {
    if (!newSessionName.trim() || sessionGroupSelected.size < 2) return;
    const session = {
      // Date.now() alone collides on rapid double-click (sub-ms span);
      // append a random suffix to make collisions practically impossible.
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: newSessionName.trim(),
      thesis: newSessionThesis.trim(),
      date: selectedDate,
      color: newSessionColor,
      createdAt: new Date().toISOString()
    };
    setSessions(prev => [...prev, session]);
    // Assign sessionId to selected trades
    setTrades(prev => prev.map(t =>
      sessionGroupSelected.has(t.id) ? { ...t, sessionId: session.id } : t
    ));
    // Reset
    setShowCreateSession(false);
    setSessionGroupMode(false);
    setSessionGroupSelected(new Set());
    setNewSessionName('');
    setNewSessionColor('blue');
    setNewSessionThesis('');
  };

  // Apply membership: trades in selection are added to session;
  // trades that are currently in the session but NOT in selection are removed from it.
  // This makes "unchecking a trade in pending mode" act as a remove-from-group action.
  const addToSession = (sessionId) => {
    setTrades(prev => prev.map(t => {
      if (t.date !== selectedDate) return t;
      const isSelected = sessionGroupSelected.has(t.id);
      const wasInSession = t.sessionId === sessionId;
      if (isSelected) return { ...t, sessionId };
      if (wasInSession && !isSelected) return { ...t, sessionId: undefined };
      return t;
    }));
    setSessionGroupMode(false);
    setSessionGroupSelected(new Set());
    setPendingAddSessionId(null);
  };

  // Rename a session. Empty/whitespace-only names are rejected silently —
  // the input field shows the original value on blur, so the user sees their
  // change didn't stick. We still log so a stuck "rename did nothing" report
  // has breadcrumbs.
  const renameSession = (sessionId, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed) {
      console.warn(`[Journal] renameSession: empty name rejected for session ${sessionId}`);
      return;
    }
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, name: trimmed } : s));
  };

  // Update a session's thesis (empty string is allowed — clears thesis)
  const updateSessionThesis = (sessionId, newThesis) => {
    const cleaned = (newThesis || '').trim();
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, thesis: cleaned } : s));
  };

  // Update a session's color. Color picker can only emit valid keys, so an
  // invalid `newColor` here means a programming error (typo in the picker UI,
  // a stale color saved on a session, etc.). Log loudly so the bug surfaces.
  const updateSessionColor = (sessionId, newColor) => {
    if (!SESSION_COLORS[newColor]) {
      console.warn(`[Journal] updateSessionColor: unknown color "${newColor}" for session ${sessionId}; expected one of ${Object.keys(SESSION_COLORS).join(', ')}`);
      return;
    }
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, color: newColor } : s));
  };

  // Update a session's post-trade lessons (empty string is allowed — clears lessons)
  const updateSessionLessons = (sessionId, newLessons) => {
    const cleaned = (newLessons || '').trim();
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, lessons: cleaned } : s));
  };

  // Update a session's process rating ('good' | 'neutral' | 'bad' | null to clear)
  const updateSessionRating = (sessionId, newRating) => {
    const allowed = ['good', 'neutral', 'bad'];
    const cleaned = allowed.includes(newRating) ? newRating : null;
    setSessions(prev => prev.map(s => {
      if (s.id !== sessionId) return s;
      // Toggle off if same rating clicked
      if (s.rating === cleaned) {
        // Strip the `rating` field via rest-spread; the destructured name
        // is intentionally unused (renamed to satisfy the lint guard).
        const { rating: _omittedRating, ...rest } = s;
        return rest;
      }
      return { ...s, rating: cleaned };
    }));
  };

  // Persist an AI-generated insight on the session ({ text, generatedAt })
  const setSessionAiInsight = (sessionId, insight) => {
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, aiInsight: insight } : s));
  };

  // -----------------------------------------------------------------------
  // AI Coach — real-time auto-refresh (Phase 2A)
  // -----------------------------------------------------------------------
  // First manual click enables `autoTrack: true` on the session's aiInsight.
  // From that point on, any change to the session's trades / thesis / rating /
  // lessons triggers a silent debounced re-run (500 ms after last change).
  // The X button clears the insight which un-tracks the session.
  // Refs keep the latest sessions/trades reachable inside timer callbacks so
  // a rapid burst of edits doesn't race against a stale closure.
  const sessionsRef       = useRef(sessions);
  const tradesRef         = useRef(trades);
  const allTradesRef      = useRef(allTrades);
  const allConfluencesRef = useRef(allConfluences);
  const mistakesRef       = useRef(mistakes);
  const hideAccountsRef   = useRef(hideAccounts);
  const setupDetailsRef   = useRef(setupDetails);
  const autoRefreshTimersRef = useRef(new Map()); // sessionId → timeoutId
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);
  useEffect(() => { tradesRef.current = trades; }, [trades]);
  useEffect(() => { allTradesRef.current = allTrades; }, [allTrades]);
  useEffect(() => { allConfluencesRef.current = allConfluences; }, [allConfluences]);
  useEffect(() => { mistakesRef.current = mistakes; }, [mistakes]);
  useEffect(() => { hideAccountsRef.current = hideAccounts; }, [hideAccounts]);
  useEffect(() => { setupDetailsRef.current = setupDetails; }, [setupDetails]);

  // Cleanup any pending timers on unmount so we don't fire into a gone tree.
  useEffect(() => () => {
    autoRefreshTimersRef.current.forEach(id => clearTimeout(id));
    autoRefreshTimersRef.current.clear();
  }, []);

  // Core analysis run — shared between manual generate + silent refresh so the
  // analyzer context stays identical across both code paths.
  const runCoachForSession = (sessionObj, sessionTrades) => {
    const observations = coach.analyze(
      { type: 'session', id: sessionObj.id },
      {
        session: sessionObj,
        trades: sessionTrades,
        allTrades: allTradesRef.current || tradesRef.current,
        allConfluences: allConfluencesRef.current,
        allMistakes: mistakesRef.current,
        // Powers confluenceConsensus's "is this already pinned?" check so the
        // Pin action button correctly disappears after the pin succeeds.
        setupDetails: setupDetailsRef.current,
        getTradeNetPnL,
        getAccountById,
        hideAccounts: hideAccountsRef.current,
        top: 3
      }
    );
    return observations;
  };

  // Silent refresh — no loading spinner, no visible flash. Re-reads latest
  // state from refs so a debounced fire from 500 ms ago still runs against
  // the freshest data. Bails if the session has been untracked since we
  // scheduled the timer (race guard).
  const silentRefreshSessionAiInsight = (sessionId) => {
    const sessionObj = sessionsRef.current.find(s => s.id === sessionId);
    if (!sessionObj?.aiInsight?.autoTrack) return; // Untracked — bail

    const sessionTrades = (tradesRef.current || []).filter(t => t.sessionId === sessionId);
    // Include setupDetails in the signature so a pin/unpin (which changes
    // recommendedConfluences but doesn't touch trades) still triggers a
    // refresh — otherwise the cached observation persists with a stale
    // Pin action button.
    const signature = buildSessionSignature(sessionObj, sessionTrades, setupDetailsRef.current);
    // Bail if nothing actually changed between schedule + fire
    if (signature === sessionObj.aiInsight.signature) return;

    try {
      const observations = runCoachForSession(sessionObj, sessionTrades);
      setSessionAiInsight(sessionId, {
        observations,
        text: coach.narrate(observations),
        generatedAt: sessionObj.aiInsight.generatedAt || new Date().toISOString(),
        refreshedAt: new Date().toISOString(),
        autoTrack: true,
        signature
      });
    } catch (err) {
      console.error('[Coach] silentRefreshSessionAiInsight failed:', err);
    }
  };

  // Watcher effect — whenever sessions or trades change, walk every tracked
  // session and either:
  //   (a) Freeze it if it's from a prior trading day (autoTrack → false).
  //       Historical coaching should be preserved as-is; only today's
  //       sessions stay Live by default.
  //   (b) Schedule a debounced silent refresh if the signature drifted.
  //   (c) Cancel any pending timer if untracked or back in sync.
  useEffect(() => {
    const timers = autoRefreshTimersRef.current;
    const freezeIds = []; // historical sessions that need one-time autoTrack→false

    sessions.forEach(session => {
      const insight = session.aiInsight;

      // Historical session with Live on? Flag for freeze (batched below).
      const isToday = !session.date || !currentTradingDay || session.date === currentTradingDay;
      if (insight?.autoTrack && !isToday) {
        // Cancel any pending timer — no refresh, no overwrite of historical coaching
        if (timers.has(session.id)) {
          clearTimeout(timers.get(session.id));
          timers.delete(session.id);
        }
        freezeIds.push(session.id);
        return;
      }

      if (!insight?.autoTrack) {
        // Untracked — kill any pending timer for this session
        if (timers.has(session.id)) {
          clearTimeout(timers.get(session.id));
          timers.delete(session.id);
        }
        return;
      }

      const sessionTrades = trades.filter(t => t.sessionId === session.id);
      // setupDetails included so pin/unpin invalidates the cache (it changes
      // recommendedConfluences without touching trade fields).
      const currentSig = buildSessionSignature(session, sessionTrades, setupDetails);
      if (currentSig === insight.signature) {
        // In sync — kill any stale pending timer (edit that got reverted)
        if (timers.has(session.id)) {
          clearTimeout(timers.get(session.id));
          timers.delete(session.id);
        }
        return;
      }

      // Drift detected — reset debounce (supersede any pending fire)
      if (timers.has(session.id)) clearTimeout(timers.get(session.id));
      const timerId = setTimeout(() => {
        silentRefreshSessionAiInsight(session.id);
        timers.delete(session.id);
      }, 500);
      timers.set(session.id, timerId);
    });

    // Commit any historical freezes in a single batch state update. The
    // subsequent effect run will see autoTrack=false and skip these cleanly.
    if (freezeIds.length > 0) {
      const nowIso = new Date().toISOString();
      setSessions(prev => prev.map(s => {
        if (!freezeIds.includes(s.id) || !s.aiInsight?.autoTrack) return s;
        return {
          ...s,
          aiInsight: {
            ...s.aiInsight,
            autoTrack: false,
            pausedAt: s.aiInsight.pausedAt || nowIso,
            pauseReason: 'historical'   // helps future UX ("frozen because past trading day")
          }
        };
      }));
    }
    // setSessions is stable from useState; silentRefreshSessionAiInsight is an
    // arrow defined inline above and re-referenced via the live ref pattern,
    // so including it would cause this effect to fire on every render.
    // setupDetails is included so pin/unpin actions trigger the signature
    // recomputation + debounced refresh (without it, the cached observation
    // would persist with a stale "Pin to X" button after the pin succeeded).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, trades, currentTradingDay, setupDetails]);

  // Pause live coaching for a session — keeps the existing observations
  // visible (they're the valuable artifact) but stops the watcher from
  // auto-refreshing on trade changes. User can resume anytime.
  const pauseSessionAiCoaching = (sessionId) => {
    setSessions(prev => prev.map(s => {
      if (s.id !== sessionId || !s.aiInsight) return s;
      return { ...s, aiInsight: { ...s.aiInsight, autoTrack: false, pausedAt: new Date().toISOString() } };
    }));
  };

  // Resume live coaching for a session — flips autoTrack back on and does
  // a fresh analyze immediately (so user sees the latest observations
  // without having to edit a trade to trigger the watcher).
  const resumeSessionAiCoaching = (sessionObj) => {
    if (!sessionObj?.id) return;
    try {
      const sessionTrades = (trades || []).filter(t => t.sessionId === sessionObj.id);
      const observations = runCoachForSession(sessionObj, sessionTrades);
      const signature = buildSessionSignature(sessionObj, sessionTrades, setupDetails);
      setSessionAiInsight(sessionObj.id, {
        ...(sessionObj.aiInsight || {}),
        observations,
        text: coach.narrate(observations),
        refreshedAt: new Date().toISOString(),
        autoTrack: true,
        signature,
        pausedAt: null
      });
    } catch (err) {
      console.error('[Coach] resumeSessionAiCoaching failed:', err);
    }
  };

  // Generate a coach-powered insight for a session (manual trigger). First
  // click turns on `autoTrack: true` so subsequent trade/thesis edits
  // auto-refresh without another click.
  const generateSessionAiInsight = (sessionObj) => {
    if (!sessionObj?.id) return;
    setAiInsightLoading(prev => { const next = new Set(prev); next.add(sessionObj.id); return next; });

    // Mimic async UX (lets the spinner spin briefly even though we're synchronous)
    setTimeout(() => {
      try {
        const sessionTrades = (trades || []).filter(t => t.sessionId === sessionObj.id);
        const observations = runCoachForSession(sessionObj, sessionTrades);
        const signature = buildSessionSignature(sessionObj, sessionTrades, setupDetails);

        // Persist both the structured observations (rich UI) and a narrated
        // text string (legacy slot), plus autoTrack + signature so the
        // watcher effect keeps this session live from here on.
        setSessionAiInsight(sessionObj.id, {
          observations,
          text: coach.narrate(observations),
          generatedAt: new Date().toISOString(),
          autoTrack: true,
          signature
        });
      } catch (err) {
        // Coach Engine failure shouldn't swallow silently — log and surface a
        // minimal error insight so the user sees *something* happened.
        console.error('[Coach] generateSessionAiInsight failed:', err);
        setSessionAiInsight(sessionObj.id, {
          observations: [],
          text: `AI coach hit an error: ${err?.message || 'unknown'}. Check console.`,
          generatedAt: new Date().toISOString()
        });
      } finally {
        setAiInsightLoading(prev => { const next = new Set(prev); next.delete(sessionObj.id); return next; });
      }
    }, 300);
  };

  // Begin pending add-to-existing: auto-check all trades from the chosen session for the day.
  // Caller passes the raw <select> value (always a string). Sessions created post-collision-fix
  // have string ids like "1714567890123-a3f2x9"; older sessions saved before the fix may still
  // have numeric ids. Look up via String() coercion so both types match, then store the
  // canonical id (preserving its original type) so downstream `t.sessionId === id` comparisons
  // still work against the trade records that were stamped with that exact id.
  const beginAddToExisting = (rawId) => {
    const session = findSessionById(sessions, rawId);
    if (!session) {
      // No session match: either the dropdown was set to the empty option, or
      // we have a state-sync bug (dropdown options come from `sessions` so a
      // miss in normal flow shouldn't happen). Log only the bug case.
      if (rawId) {
        console.warn(`[Journal] beginAddToExisting: session id ${rawId} not found in sessions list`);
      }
      setPendingAddSessionId(null);
      return;
    }
    setPendingAddSessionId(session.id);
    // Auto-check all dayTrades that already belong to this session, plus whatever is already selected
    setSessionGroupSelected(prev => {
      const next = new Set(prev);
      dayTrades.forEach(t => { if (t.sessionId === session.id) next.add(t.id); });
      return next;
    });
  };

  // Delete a session entirely
  const deleteSession = (sessionId) => {
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    setTrades(prev => prev.map(t =>
      t.sessionId === sessionId ? { ...t, sessionId: undefined } : t
    ));
  };

  // Toggle session group mode (mutually exclusive with confluence copy mode)
  const toggleSessionGroupMode = () => {
    if (confluenceCopySource) exitConfluenceCopyMode();
    setSessionGroupMode(prev => {
      if (prev) {
        setSessionGroupSelected(new Set());
        setPendingAddSessionId(null);
      }
      return !prev;
    });
  };

  // Helper to get confluence name from ID
  const getConfluenceName = (id) => {
    const conf = allConfluences.find(c => c.id === id);
    return conf ? conf.name : id;
  };

  // Enter copy mode — source trade's confluences will be copied
  const enterConfluenceCopyMode = (sourceTrade, _dayTrades) => {
    const sourceConf = sourceTrade.confluences || [];
    const sourceCounter = sourceTrade.counterConfluences || [];
    if (sourceConf.length === 0 && sourceCounter.length === 0) return;
    setConfluenceCopySource(sourceTrade);
    // Start with no trades selected — user picks which ones to copy to
    setConfluenceCopySelected(new Set());
  };

  // Exit copy mode
  const exitConfluenceCopyMode = () => {
    setConfluenceCopySource(null);
    setConfluenceCopySelected(new Set());
  };

  // Toggle a trade's selection in copy mode
  const toggleConfluenceCopyTrade = (tradeId) => {
    setConfluenceCopySelected(prev => {
      const next = new Set(prev);
      if (next.has(tradeId)) next.delete(tradeId);
      else next.add(tradeId);
      return next;
    });
  };

  // Apply confluence copy to target trade IDs
  const applyConfluenceCopy = (targetIds) => {
    if (!confluenceCopySource) {
      console.warn('[Journal] applyConfluenceCopy: no source trade set; copy mode UI should have prevented this');
      return;
    }
    if (targetIds.length === 0) {
      console.warn('[Journal] applyConfluenceCopy: no target trades selected; nothing to copy');
      return;
    }

    // Implied confluences (Wave E.1) are setup-definitional chips that auto-apply
    // on save — they represent the setup's identity, not the trader's per-trade
    // choices. Excluding them from the copy operation does two useful things:
    //   1. Avoids polluting the target trade with chips that aren't tied to ITS
    //      setup. If you copy "15M VWAP" implied chips onto a "5M EMA Stack"
    //      trade, those chips don't belong there — they're definitional to a
    //      different setup.
    //   2. If the target trade shares the source's setup, the same implied
    //      chips will auto-apply at save time anyway. Copying them adds zero
    //      information.
    // Counter-confluences are always user-chosen (no implied equivalent), so
    // they're copied as-is.
    const sourceSetupDef = setupDetails?.[confluenceCopySource.setup];
    const sourceImplied = Array.isArray(sourceSetupDef?.impliedConfluences)
      ? sourceSetupDef.impliedConfluences
      : [];
    const sourceConf = (confluenceCopySource.confluences || []).filter(c => !sourceImplied.includes(c));
    const sourceCounter = confluenceCopySource.counterConfluences || [];

    setTrades(prev => prev.map(t => {
      if (!targetIds.includes(t.id)) return t;
      // Merge mode: add new without removing existing
      const mergedConf = [...new Set([...(t.confluences || []), ...sourceConf])];
      const mergedCounter = [...new Set([...(t.counterConfluences || []), ...sourceCounter])];
      // Enforce mutual exclusivity — source classification wins
      const finalConf = mergedConf.filter(c => !sourceCounter.includes(c));
      const finalCounter = mergedCounter.filter(c => !sourceConf.includes(c));
      sourceConf.forEach(c => { if (!finalConf.includes(c)) finalConf.push(c); });
      sourceCounter.forEach(c => { if (!finalCounter.includes(c)) finalCounter.push(c); });
      return { ...t, confluences: finalConf, counterConfluences: finalCounter };
    }));

    exitConfluenceCopyMode();
  };
  
  // Use passed eventTags if available, otherwise fall back to legacy EVENT_TAGS constant
  const availableEventTags = eventTags.length > 0 ? eventTags : EVENT_TAGS;

  // Build a historical aggregate keyed by lowercase session name (excludes today's session
  // so we compare today vs prior). Each entry: { name, sessionCount, totalPnl, avgPnl, totalTrades }
  // A "session" is one entry in the sessions array (one named group from one day).
  const historicalSessionStats = useMemo(() => {
    const byName = new Map();
    const tradesBySessionId = new Map();
    (allTrades || trades).forEach(t => {
      if (!t.sessionId) return;
      if (!tradesBySessionId.has(t.sessionId)) tradesBySessionId.set(t.sessionId, []);
      tradesBySessionId.get(t.sessionId).push(t);
    });
    sessions.forEach(s => {
      const key = (s.name || '').trim().toLowerCase();
      if (!key) return;
      const sTrades = tradesBySessionId.get(s.id) || [];
      const pnl = sTrades.reduce((sum, t) => {
        const acc = getAccountById(t.accountId);
        const { netPnl } = getTradeNetPnL(t, acc);
        return sum + (isNaN(netPnl) ? 0 : netPnl);
      }, 0);
      if (!byName.has(key)) byName.set(key, { name: s.name, sessions: [], totalPnl: 0, totalTrades: 0 });
      const row = byName.get(key);
      row.sessions.push({ id: s.id, date: s.date, pnl, count: sTrades.length });
      row.totalPnl += pnl;
      row.totalTrades += sTrades.length;
    });
    // Add derived avg
    byName.forEach(row => {
      row.sessionCount = row.sessions.length;
      row.avgPnl = row.sessionCount > 0 ? row.totalPnl / row.sessionCount : 0;
    });
    return byName;
  }, [allTrades, trades, sessions, getAccountById]);

  // Support both controlled (from parent) and uncontrolled (internal) date state.
  // Fallback uses getCurrentTradingDay() instead of `new Date().toISOString().split('T')[0]`:
  // the latter returns the UTC calendar day, which on the US west coast jumps to "tomorrow"
  // around 5pm local — first paint would show the wrong date until currentTradingDay arrives.
  const [internalSelectedDate, setInternalSelectedDate] = useState(currentTradingDay || getCurrentTradingDay());
  const selectedDate = selectedDateProp !== undefined ? selectedDateProp : internalSelectedDate;
  const setSelectedDate = setSelectedDateProp !== undefined ? setSelectedDateProp : setInternalSelectedDate;
  
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editPlan, setEditPlan] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Get all unique dates that have trades or journal entries
  const allDates = useMemo(() => {
    const dateSet = new Set();
    
    // Add dates from trades
    trades.forEach(trade => {
      if (!selectedAccount || trade.accountId === selectedAccount) {
        dateSet.add(trade.date);
      }
    });
    
    // Add dates from journal entries
    Object.keys(journalEntries || {}).forEach(date => {
      dateSet.add(date);
    });
    
    // Convert to array and sort descending (newest first)
    return Array.from(dateSet).sort((a, b) => b.localeCompare(a));
  }, [trades, journalEntries, selectedAccount]);

  // Get trades for selected date, sorted by execution time (most recent first)
  // Grouped trades are clustered together, anchored to the most-recent trade of the group
  const dayTrades = useMemo(() => {
    const filtered = trades.filter(trade => {
      const matchesDate = trade.date === selectedDate;
      const matchesAccount = !selectedAccount || trade.accountId === selectedAccount;
      return matchesDate && matchesAccount;
    });

    // Wall-clock execution instant for sorting — single implementation lives
    // in timeService.getExecutionInstantMs (returns 0 for missing entryTime).
    const getExecutionTimestamp = (trade) =>
      trade.entryTime ? getExecutionInstantMs(trade.date, trade.entryTime) : 0;

    // Compute an anchor timestamp per session: the most-recent exec time across all
    // day-trades in that session. All trades in the group will share this anchor for
    // primary sorting, so they cluster together at the position of the group's most
    // recent trade.
    const sessionAnchor = new Map();
    filtered.forEach(t => {
      if (!t.sessionId) return;
      const ts = getExecutionTimestamp(t);
      const prev = sessionAnchor.get(t.sessionId) || 0;
      if (ts > prev) sessionAnchor.set(t.sessionId, ts);
    });

    // Sort: open (in-progress) trades pinned to top, then by group anchor (most recent first),
    // then by individual exec time within the group (most recent first)
    return filtered.slice().sort((a, b) => {
      // Pin open trades to the top
      if (a.isOpen && !b.isOpen) return -1;
      if (!a.isOpen && b.isOpen) return 1;

      const aOwn = getExecutionTimestamp(a);
      const bOwn = getExecutionTimestamp(b);
      const aAnchor = a.sessionId ? sessionAnchor.get(a.sessionId) || aOwn : aOwn;
      const bAnchor = b.sessionId ? sessionAnchor.get(b.sessionId) || bOwn : bOwn;

      // Primary: by anchor (group cluster position) DESC
      if (aAnchor !== bAnchor) return bAnchor - aAnchor;

      // Tie-breaker for trades sharing the same anchor:
      //   - Both in same session → sort by own exec time DESC (within-group order)
      //   - One in session, one not → group trade comes first (so the cluster stays contiguous)
      //   - Neither in session → sort by own exec time DESC
      if (a.sessionId && b.sessionId && a.sessionId === b.sessionId) {
        return bOwn - aOwn;
      }
      if (a.sessionId && !b.sessionId) return -1;
      if (!a.sessionId && b.sessionId) return 1;
      return bOwn - aOwn;
    });
  }, [trades, selectedDate, selectedAccount]);

  // Detect trades hidden by account filter (e.g., Active Only hiding Blown accounts)
  const hiddenTradeInfo = useMemo(() => {
    if (!allTrades || allTrades.length === trades.length) return null; // No filtering active
    const allDayTrades = allTrades.filter(t => {
      const matchesDate = t.date === selectedDate;
      const matchesAccount = !selectedAccount || t.accountId === selectedAccount;
      return matchesDate && matchesAccount;
    });
    const hiddenCount = allDayTrades.length - dayTrades.length;
    if (hiddenCount <= 0) return null;
    // Identify which accounts are hidden
    const visibleIds = new Set(dayTrades.map(t => t.id));
    const hiddenTrades = allDayTrades.filter(t => !visibleIds.has(t.id));
    const hiddenAccounts = [...new Set(hiddenTrades.map(t => {
      const acc = getAccountById(t.accountId);
      return acc ? `${acc.name} (${acc.status})` : t.accountId;
    }))];
    return { count: hiddenCount, accounts: hiddenAccounts };
  }, [allTrades, trades, dayTrades, selectedDate, selectedAccount, getAccountById]);

  // Get journal entry for selected date — memoized + frozen default. Without
  // this, every JournalView re-render produced a fresh `journalEntry` object
  // (when no entry existed) which then propagated as a new prop reference to
  // every child reading it.
  const journalEntry = useMemo(
    () => journalEntries?.[selectedDate] || EMPTY_JOURNAL_ENTRY,
    [journalEntries, selectedDate]
  );

  // Latest merged events from the EconomicEventsPanel (saved + API-fetched,
  // filtered by user's currency/impact settings). Plumbed up via the panel's
  // onDisplayEventsChange callback so the auto-tag effect below can tag
  // against API events the user is seeing — not just the saved subset.
  // Without this plumbing, API-only event days (the common case) silently
  // failed to auto-tag.
  const [panelDisplayEvents, setPanelDisplayEvents] = useState(EMPTY_NEWS_EVENTS);
  // Reset when the date changes — stale events from the previous day shouldn't
  // bleed into the new day's auto-tag pass before the panel re-emits.
  useEffect(() => { setPanelDisplayEvents(EMPTY_NEWS_EVENTS); }, [selectedDate]);

  // ── Auto-tag trades near news/economic events ─────────────────────────────
  // When a trade is entered within the proximity window (default 30 min) of an
  // event on this day, add that event's name to `trade.tags` automatically.
  // Idempotent: never re-adds a tag that's already there, and does nothing when
  // there is no applicable change — safe to run whenever events/trades update.
  //
  // Uses the panel's full displayEvents when available (API + saved) and falls
  // back to journalEntry.newsEvents (saved only) when the panel hasn't emitted
  // yet. This way trades get tagged against EVERY visible event, not just the
  // user-saved subset.
  const dayEventsKey = useMemo(() => {
    const fromPanel = Array.isArray(panelDisplayEvents) ? panelDisplayEvents : [];
    const fromSaved = Array.isArray(journalEntry?.newsEvents) ? journalEntry.newsEvents : [];
    const evts = fromPanel.length > 0 ? fromPanel : fromSaved;
    return evts.map(e => `${e?.name || ''}@${e?.time || ''}`).join('|');
  }, [panelDisplayEvents, journalEntry?.newsEvents]);

  // Stable key over just the dimensions the auto-tag effect cares about:
  // trade id + entry time. Notably NOT the `tags` array — without this,
  // dep'ing on raw `dayTrades` meant the effect's own setTrades call
  // produced a new array reference and the effect re-ran (saved only by
  // idempotency of `tagTradesWithEvent`, which is fragile if anyone ever
  // changes that helper).
  const dayTradesAutoTagKey = useMemo(() => {
    return dayTrades.map(t => `${t.id}@${t.entryTime || ''}`).join('|');
  }, [dayTrades]);

  useEffect(() => {
    const fromPanel = Array.isArray(panelDisplayEvents) ? panelDisplayEvents : [];
    const fromSaved = Array.isArray(journalEntry?.newsEvents) ? journalEntry.newsEvents : [];
    const events = fromPanel.length > 0 ? fromPanel : fromSaved;
    if (events.length === 0 || dayTrades.length === 0) return;
    const tagged = autoTagTradesFromEvents(dayTrades, events, { windowMin: DEFAULT_PROXIMITY_MIN });
    const { changedCount } = diffTagApplications(dayTrades, tagged);
    if (changedCount === 0) return;
    // Map updates back into the full trades array
    const byId = new Map(tagged.map(t => [t.id, t]));
    setTrades(prev => prev.map(t => byId.has(t.id) ? byId.get(t.id) : t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayEventsKey, dayTradesAutoTagKey, setTrades]);

  // ── Manual: tag every day trade with one event ────────────────────────────
  const tagAllTradesWithEvent = (event) => {
    if (!event || !event.name) {
      console.warn('[Journal] tagAllTradesWithEvent: missing event or event.name', event);
      return;
    }
    if (dayTrades.length === 0) {
      console.warn(`[Journal] tagAllTradesWithEvent: no trades on ${selectedDate} to tag with "${event.name}"`);
      return;
    }
    const tagged = tagTradesWithEvent(dayTrades, event, { includeAll: true });
    const { changedCount } = diffTagApplications(dayTrades, tagged);
    if (changedCount === 0) return;
    const byId = new Map(tagged.map(t => [t.id, t]));
    setTrades(prev => prev.map(t => byId.has(t.id) ? byId.get(t.id) : t));
  };

  // ── Manual: tag every day trade with every event (master button) ─────────
  // Accepts the full event list from the panel (which combines saved + API
  // events). Falls back to journalEntry.newsEvents if the caller doesn't
  // supply anything — matches the old behavior for any other call site.
  // Previously this only iterated journalEntry.newsEvents (user-saved
  // events), so clicking the button when only API-fetched events were
  // showing did nothing.
  const tagAllTradesWithAllEvents = (events) => {
    const evList = Array.isArray(events) && events.length > 0
      ? events
      : (Array.isArray(journalEntry?.newsEvents) ? journalEntry.newsEvents : []);
    if (evList.length === 0 || dayTrades.length === 0) return;
    let next = dayTrades;
    for (const ev of evList) {
      if (!ev || !ev.name) continue;
      next = tagTradesWithEvent(next, ev, { includeAll: true });
    }
    const { changedCount } = diffTagApplications(dayTrades, next);
    if (changedCount === 0) return;
    const byId = new Map(next.map(t => [t.id, t]));
    setTrades(prev => prev.map(t => byId.has(t.id) ? byId.get(t.id) : t));
  };

  // Calculate daily stats (exclude open/in-progress trades from P&L calculations)
  const dayStats = useMemo(() => {
    const closedDayTrades = dayTrades.filter(t => !t.isOpen);

    if (closedDayTrades.length === 0 && dayTrades.length === 0) {
      return { pnl: 0, trades: 0, wins: 0, losses: 0, winRate: 0, rMultiple: 0, setups: [], mistakes: [], openTrades: 0 };
    }

    let totalGrossPnL = 0;
    let totalCommission = 0;
    let wins = 0;
    let losses = 0;
    let totalR = 0;
    let rCount = 0;
    const setupSet = new Set();
    const mistakeSet = new Set();

    closedDayTrades.forEach(trade => {
      // Use centralized commission calculation
      const tradeAccount = getAccountById(trade.accountId);
      const { grossPnl, commission, netPnl } = getTradeNetPnL(trade, tradeAccount);
      
      totalGrossPnL += grossPnl;
      totalCommission += commission;
      
      if (netPnl > 0) wins++;
      else if (netPnl < 0) losses++;
      
      if (trade.setup) setupSet.add(trade.setup);
      if (trade.mistakes) trade.mistakes.forEach(m => mistakeSet.add(m));
      
      // Calculate R-multiple if stop loss is available
      if (trade.stopLoss && trade.entry) {
        const risk = Math.abs(parseFloat(trade.entry) - parseFloat(trade.stopLoss));
        if (risk > 0) {
          // Use getSpec(), not bracket access — getSpec internally calls
          // baseSymbol() so "MNQ 06-26" / "MNQM6" / "MNQ" all resolve safely.
          const spec = getSpec(trade.symbol);
          if (spec) {
            const riskDollars = (risk / spec.tickSize) * spec.tickValue * (parseInt(trade.quantity) || 1);
            if (riskDollars > 0) {
              totalR += netPnl / riskDollars;
              rCount++;
            }
          }
        }
      }
    });

    const totalNetPnL = totalGrossPnL - totalCommission;

    const openTradeCount = dayTrades.filter(t => t.isOpen).length;

    return {
      pnl: totalNetPnL,
      grossPnl: totalGrossPnL,
      commission: totalCommission,
      trades: dayTrades.length,
      closedTrades: closedDayTrades.length,
      openTrades: openTradeCount,
      wins,
      losses,
      winRate: closedDayTrades.length > 0 ? (wins / closedDayTrades.length * 100) : 0,
      rMultiple: rCount > 0 ? totalR / rCount : 0,
      setups: Array.from(setupSet),
      mistakes: Array.from(mistakeSet)
    };
  }, [dayTrades, futuresSpecs, getAccountById]);

  // Get stats for a specific date (for the date list)
  const getDateStats = (date) => {
    const dateTrades = trades.filter(trade => {
      const matchesDate = trade.date === date;
      const matchesAccount = !selectedAccount || trade.accountId === selectedAccount;
      return matchesDate && matchesAccount;
    });

    if (dateTrades.length === 0) {
      return { pnl: 0, trades: 0 };
    }

    // Exclude open/in-progress trades from P&L (matches detail panel behavior)
    const closedTrades = dateTrades.filter(t => !t.isOpen);

    // Calculate Net P&L using centralized commission function
    let totalNetPnL = 0;
    closedTrades.forEach(trade => {
      const tradeAccount = getAccountById(trade.accountId);
      const { netPnl } = getTradeNetPnL(trade, tradeAccount);
      totalNetPnL += netPnl;
    });

    return { pnl: totalNetPnL, trades: dateTrades.length };
  };

  // Navigate dates
  const goToPreviousDate = () => {
    const currentIndex = allDates.indexOf(selectedDate);
    if (currentIndex < allDates.length - 1) {
      setSelectedDate(allDates[currentIndex + 1]);
    }
  };

  const goToNextDate = () => {
    const currentIndex = allDates.indexOf(selectedDate);
    if (currentIndex > 0) {
      setSelectedDate(allDates[currentIndex - 1]);
    }
  };

  // Save journal entry field — useCallback'd so child components depending
  // on derivative handlers (e.g., LiveCoachFeed's onSaveDayInsight) get
  // stable references across renders. Without this, every parent render
  // produced a new function identity → new prop reference → memo bust.
  const saveJournalField = useCallback((field, value) => {
    setJournalEntries(prev => {
      const existing = prev?.[selectedDate] || EMPTY_JOURNAL_ENTRY;
      return {
        ...prev,
        [selectedDate]: { ...existing, [field]: value }
      };
    });
  }, [selectedDate, setJournalEntries]);

  // Stable handler for LiveCoachFeed's snapshot save. Identity is preserved
  // across renders so the child's seed-snapshot effect doesn't mistakenly
  // re-fire just because the parent re-rendered.
  const handleSaveDayInsight = useCallback(
    (blob) => saveJournalField('aiDayInsight', blob),
    [saveJournalField]
  );

  // Toggle a single day-level event tag — adds/removes ONLY that tag from each
  // trade on this day, leaving any per-trade event tags untouched.
  const toggleEventTag = (tag) => {
    const scrollY = window.scrollY; // Preserve scroll — re-render can shift content height
    const currentTags = journalEntry.eventTags || [];
    const isAdding = !currentTags.includes(tag);
    const newTags = isAdding
      ? [...currentTags, tag]
      : currentTags.filter(t => t !== tag);

    // Save to journal entry
    saveJournalField('eventTags', newTags);

    // Apply only this single tag to every trade on the day — do NOT touch the
    // other tags on the trade. This preserves both per-trade event tags AND any
    // other day-level event tags that were applied earlier.
    if (allTrades && setTrades) {
      setTrades(prevTrades => prevTrades.map(trade => {
        if (trade.date !== selectedDate) return trade;
        const tradeTags = trade.tags || [];
        if (isAdding) {
          // Avoid duplicates if the trade already has this tag individually
          if (tradeTags.includes(tag)) return trade;
          return { ...trade, tags: [...tradeTags, tag] };
        }
        // Removing: drop this specific tag only
        if (!tradeTags.includes(tag)) return trade;
        return { ...trade, tags: tradeTags.filter(t => t !== tag) };
      }));
    }

    // Restore scroll after React re-render — prevents header collapse/expand
    // (trade sync re-render shifts content height, which can push scrollY
    //  past the header's hysteresis threshold and change its transparency)
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="flex h-full gap-0">
      {/* Date List Sidebar — collapsible with pin */}
      <div
        className={`relative shrink-0 rounded-2xl border overflow-hidden flex flex-col transition-[background-color,border-color,box-shadow,color] duration-150 ease-in-out ${
          sidebarOpen ? 'w-64' : 'w-10'
        } ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}
        onMouseEnter={() => {
          // If already open, just cancel any pending close. Otherwise schedule an
          // open after a short delay so quick swipe-overs don't pop the sidebar out.
          if (sidebarHovered) {
            cancelSidebarClose();
          } else if (!sidebarPinned && sidebarCollapsed) {
            scheduleSidebarOpen();
          }
        }}
        onMouseLeave={() => {
          // Cancel a pending open if the cursor leaves before the open delay fires.
          cancelSidebarOpen();
          // Delay the close so quick flickers don't yank the sidebar away
          // and so we always retract reliably even if mouseleave fires inconsistently.
          if (!sidebarPinned && sidebarCollapsed && sidebarHovered) scheduleSidebarClose();
        }}
      >
        {/* Sidebar header */}
        <div className={`p-3 border-b flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'} ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {sidebarOpen ? (
            <>
              <div>
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Trading Days
                </h3>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {allDates.length} days recorded
                </p>
              </div>
              <div className="flex items-center gap-1">
                {/* Pin toggle — frosted glass icon button. When pinned the
                    button picks up the v7.5.15 "engaged" tint (blue glass +
                    ring) so it reads as a stateful control, not a plain icon. */}
                <button
                  onClick={() => setSidebarPinned(p => !p)}
                  className={`p-1.5 rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    sidebarPinned
                      ? darkMode
                        ? 'bg-blue-500/20 border-blue-400/40 text-blue-200 ring-1 ring-inset ring-blue-400/20 hover:bg-blue-500/30'
                        : 'bg-blue-50 border-blue-300 text-blue-700 ring-1 ring-inset ring-blue-200'
                      : darkMode
                        ? 'bg-white/[0.04] border-white/10 text-gray-400 hover:bg-white/[0.08] hover:border-white/20 hover:text-gray-200'
                        : 'bg-white/60 border-gray-200 text-gray-500 hover:bg-white/80 hover:border-gray-300 hover:text-gray-700'
                  }`}
                  title={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                >
                  {sidebarPinned ? <Pin size={14} /> : <PinOff size={14} />}
                </button>
                <button
                  onClick={() => { setSidebarCollapsed(true); setSidebarPinned(false); setSidebarHovered(false); }}
                  className={`p-1.5 rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    darkMode
                      ? 'bg-white/[0.04] border-white/10 text-gray-400 hover:bg-white/[0.08] hover:border-white/20 hover:text-gray-200'
                      : 'bg-white/60 border-gray-200 text-gray-500 hover:bg-white/80 hover:border-gray-300 hover:text-gray-700'
                  }`}
                  title="Collapse sidebar"
                >
                  <PanelLeftClose size={14} />
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => { setSidebarCollapsed(false); setSidebarPinned(true); }}
              className={`p-1.5 rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                darkMode
                  ? 'bg-white/[0.04] border-white/10 text-gray-400 hover:bg-white/[0.08] hover:border-white/20 hover:text-gray-200'
                  : 'bg-white/60 border-gray-200 text-gray-500 hover:bg-white/80 hover:border-gray-300 hover:text-gray-700'
              }`}
              title="Expand sidebar"
            >
              <PanelLeftOpen size={14} />
            </button>
          )}
        </div>

        {/* Sidebar content — only rendered when open */}
        {sidebarOpen && (
          <>
            {/* Add Today Button - uses trading day (futures: 6pm boundary) */}
            {currentTradingDay && !allDates.includes(currentTradingDay) && (
              <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => setSelectedDate(currentTradingDay)}
                  className={`w-full py-2 px-3 rounded-lg text-sm font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 flex items-center justify-center gap-2 ${
                    darkMode
                      ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_18px_rgba(59,130,246,0.3)]'
                      : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                  }`}
                >
                  <Plus size={16} />
                  Start Today’s Journal
                </button>
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {allDates.length === 0 ? (
                <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No trading days yet</p>
                </div>
              ) : (
                allDates.map(date => {
                  const stats = getDateStats(date);
                  const entry = journalEntries?.[date];
                  const isSelected = date === selectedDate;

                  // Check if journaling is complete for this day
                  const dateTrades = trades.filter(t => {
                    const matchesDate = t.date === date;
                    const matchesAccount = !selectedAccount || t.accountId === selectedAccount;
                    return matchesDate && matchesAccount;
                  });
                  const journaledCount = dateTrades.filter(t => {
                    const hasStopLoss = t.stopLoss || t.stopLossPoints;
                    if (t.journaled === undefined) return hasStopLoss;
                    return t.journaled === true && hasStopLoss;
                  }).length;
                  const isJournalingComplete = dateTrades.length > 0 && journaledCount === dateTrades.length;

                  return (
                    <button
                      key={date}
                      onClick={() => setSelectedDate(date)}
                      className={`w-full p-3 text-left border-b transition-colors relative ${
                        isSelected
                          ? darkMode
                        ? 'bg-blue-900/30 border-blue-800' 
                        : 'bg-blue-50 border-blue-200'
                      : darkMode
                        ? 'border-gray-700 hover:bg-gray-700/50'
                        : 'border-gray-100 hover:bg-gray-50'
                  }`}
                >
                  {/* Journaling Complete Checkmark */}
                  {isJournalingComplete && (
                    <div className="absolute top-1 left-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center shadow-xs">
                      <span className="text-white text-[10px] leading-none">✓</span>
                    </div>
                  )}
                  <div className="flex justify-between items-start">
                    <div className={isJournalingComplete ? 'pl-4' : ''}>
                      <div className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatShortDate(date)}
                      </div>
                      <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {stats.trades} trade{stats.trades !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className={`text-sm font-semibold ${
                      stats.pnl >= 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {stats.pnl >= 0 ? '+' : '-'}${fmtUSD(stats.pnl)}
                    </div>
                  </div>
                  {entry?.eventTags?.length > 0 && (
                    <div className={`flex flex-wrap gap-1 mt-2 ${isJournalingComplete ? 'pl-4' : ''}`}>
                      {entry.eventTags.slice(0, 2).map(tag => (
                        <span key={tag} className="text-xs px-1.5 py-0.5 rounded-sm bg-purple-500/20 text-purple-400">
                          {tag}
                        </span>
                      ))}
                      {entry.eventTags.length > 2 && (
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          +{entry.eventTags.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })
          )}
            </div>
          </>
        )}

        {/* Collapsed state — mini date indicators */}
        {!sidebarOpen && (
          <div className="flex-1 overflow-y-auto flex flex-col items-center py-2 gap-1">
            {allDates.slice(0, 20).map(date => {
              const stats = getDateStats(date);
              const isSelected = date === selectedDate;
              return (
                <button
                  key={date}
                  onClick={() => { setSelectedDate(date); setSidebarCollapsed(false); setSidebarPinned(true); }}
                  className={`w-6 h-1.5 rounded-full transition-colors ${
                    isSelected
                      ? 'bg-blue-500'
                      : stats.pnl >= 0 ? 'bg-green-500/40' : 'bg-red-500/40'
                  }`}
                  title={`${formatShortDate(date)} • ${stats.pnl >= 0 ? '+' : '-'}$${fmtUSD(stats.pnl)}`}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto space-y-4 ml-4">
        {/* Date Header */}
        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={goToPreviousDate}
                disabled={allDates.indexOf(selectedDate) >= allDates.length - 1}
                className={`p-2 rounded-lg transition-colors disabled:opacity-30 ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <ChevronLeft size={20} />
              </button>
              <div>
                <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatDate(selectedDate)}
                </h2>
                <div className="flex items-center gap-4 mt-1">
                  <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                    {dayStats.trades} trades
                  </span>
                  <span className={`text-sm font-semibold ${dayStats.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {dayStats.pnl >= 0 ? '+' : '-'}${fmtUSD(dayStats.pnl)}
                  </span>
                  {dayStats.rMultiple !== 0 && (
                    <span className={`text-sm ${dayStats.rMultiple >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {dayStats.rMultiple >= 0 ? '+' : '-'}{Math.abs(dayStats.rMultiple).toFixed(2)}R
                    </span>
                  )}
                  <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                    {dayStats.winRate.toFixed(0)}% Win Rate
                  </span>
                </div>
              </div>
              <button
                onClick={goToNextDate}
                disabled={allDates.indexOf(selectedDate) <= 0}
                className={`p-2 rounded-lg transition-colors disabled:opacity-30 ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <ChevronRight size={20} />
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              {/* AI Daily Review Button — frosted purple glass, matches the
                  AI Insights button in the top header. */}
              <button
                onClick={() => onShowDailyReview(selectedDate)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                  darkMode
                    ? 'bg-purple-500/25 border-purple-400/50 text-purple-50 ring-1 ring-inset ring-purple-400/25 hover:bg-purple-500/35 hover:border-purple-400/70 hover:shadow-[0_0_18px_rgba(168,85,247,0.3)]'
                    : 'bg-purple-100 border-purple-300 text-purple-800 hover:bg-purple-200 hover:border-purple-400'
                }`}
              >
                <Sparkles size={18} />
                AI Daily Review
              </button>

              {/* Add Trade — frosted blue glass for the secondary action so
                  it differentiates from the primary Add Trade up in the
                  header (which is green/CTA) without losing the affordance. */}
              <button
                onClick={() => onAddTrade(selectedDate)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                  darkMode
                    ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_18px_rgba(59,130,246,0.3)]'
                    : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                }`}
              >
                <Plus size={18} />
                Add Trade
              </button>
            </div>
          </div>
        </div>

        {/*
          Two-column layout from Market Events through EOD:
            left (flex-1, min-w-0): Market Events + News & Economic Events + Today's Plan + End of Day Notes
            right (lg+: fixed ~460–560px): Live AI Coach Feed, sticky to viewport top
          Market Events sits in the left column (not full-width above) so it
          fills vertical dead space created by a long AI Coach panel.
          On screens narrower than lg, stacks to single column — left first,
          then feed at the bottom (honors the content priority).
          `items-start` prevents the right column from vertically centering
          against a taller left column.
        */}
        {/* Coach column widths bumped in v7.5.7 to fit the multi-symbol
            watchlist (max 2 panels side-by-side). Previous widths
            (460/520/560) crowded the 2-up grid; new widths add ~80-120px
            so each compact panel gets ~280-330px of room. */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_560px] xl:grid-cols-[minmax(0,1fr)_640px] 2xl:grid-cols-[minmax(0,1fr)_700px] gap-4 items-start">

          {/* ── LEFT: Market Events + News + Plan + EOD stacked ── */}
          <div ref={leftColumnRef} className="space-y-4 min-w-0">

            {/* Event Tags / Market Events */}
            <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Tag size={18} className="text-purple-500" />
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Market Events
                </h3>
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  (applied to all trades this day)
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableEventTags.map(tag => {
                  const isActive = (journalEntry.eventTags || []).includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => toggleEventTag(tag)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium tracking-wide backdrop-blur-sm border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                        isActive
                          ? 'bg-gradient-to-b from-purple-500/90 to-purple-600/90 border-purple-400/60 text-white shadow-sm shadow-purple-500/30 ring-1 ring-purple-400/30 hover:from-purple-400/90 hover:to-purple-500/90'
                          : darkMode
                            ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.09] hover:border-white/25 hover:text-white'
                            : 'bg-white/50 border-gray-300/60 text-gray-700 hover:bg-white/80 hover:border-gray-400 hover:text-gray-900'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* News & Economic Events Section - Using modular component */}
            <EconomicEventsPanel
              darkMode={darkMode}
              newsEvents={journalEntry.newsEvents || EMPTY_NEWS_EVENTS}
              onSaveEvents={(events) => saveJournalField('newsEvents', events)}
              settings={economicCalendarSettings}
              selectedDate={selectedDate}
              dayTradeCount={dayTrades.length}
              onTagEventToAllTrades={tagAllTradesWithEvent}
              onTagAllEventsToAllTrades={tagAllTradesWithAllEvents}
              // Plumb merged events back up so auto-tag can use API events too
              onDisplayEventsChange={setPanelDisplayEvents}
            />

        {/* Plan Section */}
        <div className={`p-4 rounded-lg border text-left ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-blue-500" />
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Today’s Plan
              </h3>
              {/* Phase 15 — Coach-suggested plan generator. Hidden when LLM
                  isn't configured. Sits inline so the plan editor stays the
                  primary action; this is an assist, not a replacement. */}
              {coachLLMSettings?.enabled && coachLLMSettings?.apiKey && (
                <PlanGeneratorButton
                  profile={coachProfile}
                  recentTrades={(allTrades || []).filter(t => t?.date && selectedDate && t.date <= selectedDate).slice(-50)}
                  todayEvents={journalEntry?.newsEvents || []}
                  dateStr={selectedDate}
                  apiKey={coachLLMSettings.apiKey}
                  model={coachLLMSettings.model}
                  voice={coachLLMSettings.voice}
                  ttsEnabled={!!coachLLMSettings.ttsEnabled}
                  ttsSettings={coachLLMSettings.tts}
                  currentPlan={journalEntry?.plan || ''}
                  onAcceptPlan={(text, opts) => {
                    const merged = opts?.append && journalEntry?.plan
                      ? `${journalEntry.plan}\n\n— Coach suggestion —\n${text}`
                      : text;
                    saveJournalField('plan', merged);
                    // Surface the result in the editor so the trader can
                    // tweak before saving definitively (handler is idempotent).
                    setEditPlan(merged);
                  }}
                  getTradeNetPnL={getTradeNetPnL}
                  getAccountById={getAccountById}
                  darkMode={darkMode}
                />
              )}
            </div>
            {!isEditingPlan ? (
              <button
                onClick={() => {
                  setEditPlan(journalEntry.plan || '');
                  setIsEditingPlan(true);
                }}
                className={`p-1.5 rounded hover:bg-blue-500 hover:text-white transition-colors ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                <Edit3 size={16} />
              </button>
            ) : (
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    saveJournalField('plan', editPlan);
                    setIsEditingPlan(false);
                  }}
                  className="p-1.5 rounded-sm bg-green-500 text-white hover:bg-green-600 transition-colors"
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={() => setIsEditingPlan(false)}
                  className={`p-1.5 rounded transition-colors ${
                    darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
          {isEditingPlan ? (
            <RichTextEditor
              value={editPlan}
              onChange={setEditPlan}
              placeholder="What's your trading plan for today? Key levels, setups to watch, rules to follow..."
              darkMode={darkMode}
              minHeight="120px"
              templateType="todaysPlan"
            />
          ) : (
            <RichTextDisplay
              content={journalEntry.plan}
              darkMode={darkMode}
              emptyMessage="No plan set for this day. Click edit to add one."
            />
          )}
        </div>

        {/* End of Day Notes */}
        <div className={`p-4 rounded-lg border text-left ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-yellow-500" />
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                End of Day Notes
              </h3>
            </div>
            {!isEditingNotes ? (
              <button
                onClick={() => {
                  setEditNotes(journalEntry.notes || '');
                  setIsEditingNotes(true);
                }}
                className={`p-1.5 rounded hover:bg-blue-500 hover:text-white transition-colors ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                <Edit3 size={16} />
              </button>
            ) : (
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    saveJournalField('notes', editNotes);
                    setIsEditingNotes(false);
                  }}
                  className="p-1.5 rounded-sm bg-green-500 text-white hover:bg-green-600 transition-colors"
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={() => setIsEditingNotes(false)}
                  className={`p-1.5 rounded transition-colors ${
                    darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
          {isEditingNotes ? (
            <RichTextEditor
              value={editNotes}
              onChange={setEditNotes}
              placeholder="Reflect on your trading day. What went well? What could be improved? Key lessons learned..."
              darkMode={darkMode}
              minHeight="120px"
              templateType="eodNotes"
            />
          ) : (
            <RichTextDisplay
              content={journalEntry.notes}
              darkMode={darkMode}
              emptyMessage="No end of day notes. Take a moment to reflect on your trading."
            />
          )}
        </div>

          </div>  {/* close LEFT column (News + Plan + EOD stack) */}

          {/* ── RIGHT: Live AI Coach Feed ──
              Sticky so it stays visible as the user scrolls through the left
              column. Internal scroll via max-h on the panel itself. */}
          <LiveCoachFeed
            sessions={sessions}
            playbooks={playbooks}
            selectedDate={selectedDate}
            currentTradingDay={currentTradingDay}
            dayTrades={dayTrades}
            allTrades={allTrades}
            journalEntries={journalEntries}
            accounts={accounts}
            getTradeNetPnL={getTradeNetPnL}
            getAccountById={getAccountById}
            userTier={userTier}
            ntTemplateVersions={ntTemplateVersions}
            onJumpToSession={jumpToSession}
            onShowDailyReview={onShowDailyReview}
            onSaveDayInsight={handleSaveDayInsight}
            onOpenLicenseSettings={onOpenLicenseSettings}
            onOpenCoachDashboard={onOpenCoachDashboard}
            onOpenWeeklyDigest={onOpenWeeklyDigest}
            onOpenWhatIfBacktest={onOpenWhatIfBacktest}
            coachHealth={coachHealth}
            maxHeight={leftColumnHeight}
            darkMode={darkMode}
          />

        </div>  {/* close 2-col grid */}

        {/* Trades Section */}
        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {dayStats.pnl >= 0 ? (
                <TrendingUp size={18} className="text-green-500" />
              ) : (
                <TrendingDown size={18} className="text-red-500" />
              )}
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Today’s Trades
              </h3>
              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ({dayTrades.length})
              </span>
              {/* Journaling Progress Counter */}
              {dayTrades.length > 0 && (() => {
                const openCount = dayTrades.filter(t => t.isOpen).length;
                const closedTrades = dayTrades.filter(t => !t.isOpen);
                const journaledCount = closedTrades.filter(t => {
                  const hasStopLoss = t.stopLoss || t.stopLossPoints;
                  if (t.journaled === undefined) return hasStopLoss;
                  return t.journaled === true && hasStopLoss;
                }).length;
                const isComplete = closedTrades.length > 0 && journaledCount === closedTrades.length && openCount === 0;
                return (
                  <span className="flex items-center gap-2">
                    <span className={`text-sm px-2 py-0.5 rounded ${
                      isComplete
                        ? 'bg-green-500/20 text-green-500'
                        : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {isComplete ? '✓ Journaling Complete!' : `${journaledCount}/${closedTrades.length} Journaled`}
                    </span>
                    {openCount > 0 && (
                      <span className="text-sm px-2 py-0.5 rounded-sm bg-yellow-500/20 text-yellow-500 flex items-center gap-1">
                        <Activity size={12} className="animate-pulse" />
                        {openCount} Live
                      </span>
                    )}
                    {/* Hide/Show Account Numbers */}
                    <button
                      onClick={() => setHideAccounts(prev => !prev)}
                      className={`p-1 rounded transition-colors ${
                        hideAccounts
                          ? darkMode ? 'text-yellow-400 hover:bg-yellow-500/20' : 'text-yellow-600 hover:bg-yellow-100'
                          : darkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      }`}
                      title={hideAccounts ? 'Show account numbers' : 'Hide account numbers'}
                    >
                      {hideAccounts ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </span>
                );
              })()}
            </div>

            {/* Session Group Mode Toggle */}
            {!confluenceCopySource && dayTrades.length >= 2 && (
              <button
                onClick={toggleSessionGroupMode}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  sessionGroupMode
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                    : darkMode ? 'text-gray-400 hover:bg-gray-600 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-200'
                }`}
                title="Group trades into a session"
              >
                <Tag size={14} />
                {sessionGroupMode ? 'Cancel Grouping' : 'Group'}
              </button>
            )}

            {/* Confluence Copy Mode Action Bar */}
            {confluenceCopySource && (
              <div className={`flex items-center justify-center gap-3 py-2 px-4 rounded-lg mb-3 ${
                darkMode ? 'bg-cyan-900/30 border border-cyan-500/30' : 'bg-cyan-50 border border-cyan-200'
              }`}>
                <div className={`text-xs ${darkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                  <Copy size={12} className="inline mr-1" />
                  Copying from <span className="font-semibold">#{dayTrades.findIndex(t => t.id === confluenceCopySource.id) + 1}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const allOtherIds = dayTrades.filter(t => t.id !== confluenceCopySource.id).map(t => t.id);
                      applyConfluenceCopy(allOtherIds);
                    }}
                    className="px-3 py-1 text-xs font-medium rounded-lg bg-cyan-500 text-white hover:bg-cyan-600 transition-colors"
                  >
                    Copy to All
                  </button>
                  <button
                    onClick={() => applyConfluenceCopy([...confluenceCopySelected])}
                    disabled={confluenceCopySelected.size === 0}
                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                      confluenceCopySelected.size > 0
                        ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                        : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Copy to Selected ({confluenceCopySelected.size})
                  </button>
                  <button
                    onClick={exitConfluenceCopyMode}
                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                      darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Session Grouping Action Bar */}
            {sessionGroupMode && sessionGroupSelected.size >= 1 && !showCreateSession && (() => {
              // Existing groups for the currently visible day (only those with at least one trade still on this day)
              const dayGroups = sessions.filter(s =>
                dayTrades.some(t => t.sessionId === s.id)
              );
              const pendingSession = pendingAddSessionId ? sessions.find(s => s.id === pendingAddSessionId) : null;
              // Compute diff for confirm-button label
              const diff = pendingSession ? (() => {
                let added = 0;
                let removed = 0;
                dayTrades.forEach(t => {
                  const isSel = sessionGroupSelected.has(t.id);
                  const wasIn = t.sessionId === pendingSession.id;
                  if (isSel && !wasIn) added++;
                  if (!isSel && wasIn) removed++;
                });
                return { added, removed };
              })() : null;
              return (
                <div className={`flex items-center justify-center gap-3 py-2 px-4 rounded-lg mb-3 ${
                  darkMode ? 'bg-blue-900/30 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'
                }`}>
                  <span className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    {sessionGroupSelected.size} trade{sessionGroupSelected.size !== 1 ? 's' : ''} selected
                  </span>
                  {/* Create Session — needs 2+ trades */}
                  <button
                    onClick={() => setShowCreateSession(true)}
                    disabled={sessionGroupSelected.size < 2}
                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                      sessionGroupSelected.size >= 2
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    title={sessionGroupSelected.size < 2 ? 'Select at least 2 trades to create a new group' : 'Create a new group from selected trades'}
                  >
                    Create Session
                  </button>
                  {/* Add to existing — works with any selection size */}
                  {dayGroups.length > 0 && (
                    <div className="min-w-[160px]">
                      <GlassSelect
                        value={pendingAddSessionId || ''}
                        onChange={(v) => beginAddToExisting(v || null)}
                        options={dayGroups.map(s => ({ value: s.id, label: s.name }))}
                        placeholder="Add to existing..."
                        darkMode={darkMode}
                        size="sm"
                      />
                    </div>
                  )}
                  {/* Confirm button — appears once a pending session is chosen */}
                  {pendingSession && (
                    <>
                      <button
                        onClick={() => addToSession(pendingSession.id)}
                        disabled={sessionGroupSelected.size === 0 && diff.removed === 0}
                        className="px-3 py-1 text-xs font-medium rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-1.5"
                        title="Apply changes to this group"
                      >
                        <Check size={12} />
                        Confirm
                        {diff && (diff.added > 0 || diff.removed > 0) && (
                          <span className="ml-1 opacity-90">
                            ({diff.added > 0 ? `+${diff.added}` : ''}{diff.added > 0 && diff.removed > 0 ? ' / ' : ''}{diff.removed > 0 ? `-${diff.removed}` : ''})
                          </span>
                        )}
                      </button>
                      <span className={`text-[11px] italic ${darkMode ? 'text-blue-300/80' : 'text-blue-700/80'}`}>
                        Uncheck a trade to remove it from “{pendingSession.name}”
                      </span>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Create Session Form */}
            {showCreateSession && (
              <div className={`p-4 rounded-lg mb-3 border ${
                darkMode ? 'bg-gray-800 border-blue-500/30' : 'bg-blue-50 border-blue-200'
              }`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Create Session ({sessionGroupSelected.size} trades)
                </h4>
                <input
                  type="text"
                  placeholder="Session name (e.g., Weekly 50 EMA Bounce)"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  className="w-full input-modern mb-3"
                  autoFocus
                />
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Color:</span>
                  {Object.entries(SESSION_COLORS).map(([key, colors]) => (
                    <button
                      key={key}
                      onClick={() => setNewSessionColor(key)}
                      className={`w-6 h-6 rounded-full ${colors.dot} transition-all ${
                        newSessionColor === key ? 'ring-2 ring-offset-2 ring-offset-gray-800 ring-white scale-110' : 'opacity-60 hover:opacity-100'
                      }`}
                    />
                  ))}
                </div>
                <textarea
                  placeholder="Thesis / notes (optional)"
                  value={newSessionThesis}
                  onChange={(e) => setNewSessionThesis(e.target.value)}
                  rows={2}
                  className="w-full input-modern mb-3"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={createSession}
                    disabled={!newSessionName.trim()}
                    className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      newSessionName.trim()
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Create
                  </button>
                  <button
                    onClick={() => { setShowCreateSession(false); setNewSessionName(''); setNewSessionThesis(''); }}
                    className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Warning: trades hidden by account filter */}
          {hiddenTradeInfo && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-2 ${
              darkMode ? 'bg-amber-900/30 border border-amber-700/50 text-amber-300' : 'bg-amber-50 border border-amber-200 text-amber-700'
            }`}>
              <AlertTriangle size={16} className="shrink-0" />
              <span>
                <strong>{hiddenTradeInfo.count} trade{hiddenTradeInfo.count !== 1 ? 's' : ''} hidden</strong> by Active Only filter
                {hiddenTradeInfo.accounts.length > 0 && (
                  <> — {hiddenTradeInfo.accounts.join(', ')}</>
                )}
              </span>
            </div>
          )}

          {dayTrades.length === 0 ? (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>No trades recorded for this day</p>
              <button
                onClick={() => onAddTrade(selectedDate)}
                className="mt-2 text-blue-500 hover:text-blue-600 text-sm"
              >
                + Add your first trade
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {(() => {
                // Track which sessions have rendered their banner
                const renderedSessionBanners = new Set();
                return dayTrades.map((trade, index) => {
                const account = getAccountById(trade.accountId);
                const rMultiple = parseFloat(trade.rMultiple) || 0;

                // Use centralized commission calculation for Net P&L
                const { netPnl } = getTradeNetPnL(trade, account);

                // Check if trade has been "journaled" - requires both stop loss AND journaled flag (for new trades)
                const isJournaled = (() => {
                  const hasStopLoss = trade.stopLoss || trade.stopLossPoints;
                  // Legacy trades (no flag): use old logic (stop loss = journaled)
                  if (trade.journaled === undefined) return hasStopLoss;
                  // New trades: require both flag AND stop loss
                  return trade.journaled === true && hasStopLoss;
                })();

                // Session info for this trade
                const tradeSession = trade.sessionId ? getSessionById(trade.sessionId) : null;
                const sessionColor = tradeSession ? getSessionColor(tradeSession) : null;
                const showSessionBanner = tradeSession && !renderedSessionBanners.has(trade.sessionId);
                if (showSessionBanner) renderedSessionBanners.add(trade.sessionId);

                // Compute session stats for banner
                const sessionBannerData = showSessionBanner ? (() => {
                  const sessionDayTrades = dayTrades.filter(t => t.sessionId === trade.sessionId);
                  // Pre-compute net P&L per trade (single pass, reused below)
                  const withPnl = sessionDayTrades.map(t => {
                    const acc = getAccountById(t.accountId);
                    const { netPnl } = getTradeNetPnL(t, acc);
                    return { trade: t, netPnl: isNaN(netPnl) ? 0 : netPnl };
                  });

                  const totalPnl = withPnl.reduce((s, x) => s + x.netPnl, 0);
                  const wins = withPnl.filter(x => x.netPnl > 0).length;
                  const losses = withPnl.filter(x => x.netPnl < 0).length;
                  const count = withPnl.length;
                  const winRate = count > 0 ? (wins / count) * 100 : 0;

                  // R-Multiple aggregate
                  const rVals = sessionDayTrades
                    .map(t => parseFloat(t.rMultiple))
                    .filter(r => isFinite(r) && r !== 0);
                  const avgR = rVals.length > 0 ? rVals.reduce((s, r) => s + r, 0) / rVals.length : null;
                  const totalR = rVals.length > 0 ? rVals.reduce((s, r) => s + r, 0) : null;

                  // Long/Short split
                  const longCount = sessionDayTrades.filter(t => t.direction === 'long').length;
                  const shortCount = sessionDayTrades.filter(t => t.direction === 'short').length;

                  // Time span: earliest entry → latest exit (fallback to entryTime)
                  const parseHM = (s) => {
                    if (!s || typeof s !== 'string') return null;
                    const str = s.includes('/') ? s.split(' ')[1] : s;
                    if (!str) return null;
                    const [h, m] = str.split(':').map(v => parseInt(v, 10));
                    if (isNaN(h) || isNaN(m)) return null;
                    return h * 60 + m;
                  };
                  const fmtHM = (min) => {
                    const h = Math.floor(min / 60);
                    const m = min % 60;
                    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                  };
                  const entryMins = sessionDayTrades.map(t => parseHM(t.entryTime)).filter(v => v !== null);
                  const exitMins = sessionDayTrades.map(t => parseHM(t.exitTime)).filter(v => v !== null);
                  const firstEntry = entryMins.length > 0 ? Math.min(...entryMins) : null;
                  const lastExit = exitMins.length > 0 ? Math.max(...exitMins) : (entryMins.length > 0 ? Math.max(...entryMins) : null);
                  const timeSpan = (firstEntry !== null && lastExit !== null)
                    ? `${fmtHM(firstEntry)}→${fmtHM(lastExit)}`
                    : null;

                  // Per-account breakdown
                  const accountMap = new Map();
                  withPnl.forEach(({ trade: t, netPnl }) => {
                    const key = t.accountId || 'unassigned';
                    if (!accountMap.has(key)) {
                      const acc = getAccountById(t.accountId);
                      accountMap.set(key, { accountId: key, name: acc?.name || t.accountId || 'Unassigned', pnl: 0, count: 0 });
                    }
                    const row = accountMap.get(key);
                    row.pnl += netPnl;
                    row.count += 1;
                  });
                  const accountBreakdown = Array.from(accountMap.values()).sort((a, b) => b.pnl - a.pnl);

                  // Equity sparkline: running P&L across trades ordered by execution time
                  const sortedForCurve = [...withPnl].sort((a, b) => {
                    const am = parseHM(a.trade.entryTime) ?? 0;
                    const bm = parseHM(b.trade.entryTime) ?? 0;
                    return am - bm;
                  });
                  let running = 0;
                  const equityPoints = sortedForCurve.map(x => {
                    running += x.netPnl;
                    return running;
                  });

                  // MAE / MFE aggregates (in points — displayed with 'pt' suffix)
                  const maeVals = sessionDayTrades
                    .map(t => parseFloat(t.mae))
                    .filter(v => isFinite(v));
                  const mfeVals = sessionDayTrades
                    .map(t => parseFloat(t.mfe))
                    .filter(v => isFinite(v));
                  const avgMAE = maeVals.length > 0 ? maeVals.reduce((s, v) => s + Math.abs(v), 0) / maeVals.length : null;
                  const avgMFE = mfeVals.length > 0 ? mfeVals.reduce((s, v) => s + Math.abs(v), 0) / mfeVals.length : null;
                  const mfeMaeRatio = (avgMAE && avgMAE !== 0 && avgMFE) ? avgMFE / avgMAE : null;

                  // Historical comparison — sessions with the same name, excluding the current one
                  const histKey = (tradeSession.name || '').trim().toLowerCase();
                  const histRow = historicalSessionStats.get(histKey);
                  const priorSessions = histRow ? histRow.sessions.filter(s => s.id !== tradeSession.id) : [];
                  const historical = priorSessions.length > 0 ? {
                    sessionCount: priorSessions.length,
                    avgPnl: priorSessions.reduce((s, x) => s + x.pnl, 0) / priorSessions.length,
                    totalPnl: priorSessions.reduce((s, x) => s + x.pnl, 0)
                  } : null;

                  // Confluence consensus — which confluences appear in ALL vs SOME trades
                  const confluenceCounts = new Map(); // id -> count
                  sessionDayTrades.forEach(t => {
                    if (Array.isArray(t.confluences)) {
                      const seen = new Set();
                      t.confluences.forEach(cid => {
                        if (!seen.has(cid)) {
                          seen.add(cid);
                          confluenceCounts.set(cid, (confluenceCounts.get(cid) || 0) + 1);
                        }
                      });
                    }
                  });
                  const confluenceConsensus = Array.from(confluenceCounts.entries())
                    .map(([id, cnt]) => {
                      const def = allConfluences.find(c => c.id === id);
                      return { id, name: def?.name || id, count: cnt, total: count, isUniversal: cnt === count };
                    })
                    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

                  return {
                    count, wins, losses, totalPnl, winRate,
                    avgR, totalR, longCount, shortCount,
                    timeSpan, accountBreakdown, equityPoints,
                    avgMAE, avgMFE, mfeMaeRatio,
                    historical, confluenceConsensus
                  };
                })() : null;

                // Is this session collapsed? (hide its trade cards but keep banner)
                const isSessionCollapsed = tradeSession && collapsedSessions.has(tradeSession.id);

                return (
                  <React.Fragment key={trade.id}>
                    {/* Session Banner — rendered before the first trade of each session.
                        Sticky (top-2) so it pins near top while scrolling through long groups,
                        sitting under the app's z-50 sticky header. */}
                    {showSessionBanner && (
                      <div
                        id={`session-banner-${tradeSession.id}`}
                        className={`sticky top-2 z-20 backdrop-blur-md rounded-lg border-l-4 ${sessionColor.border} ${sessionColor.bg} ${
                        darkMode ? 'border border-l-4 border-gray-600 bg-gray-900/70' : 'border border-l-4 border-gray-200 bg-white/80'
                      } ${sessionBannerData.totalPnl >= 0 ? 'shadow-[inset_0_0_0_1px_rgba(34,197,94,0.15)]' : 'shadow-[inset_0_0_0_1px_rgba(239,68,68,0.15)]'} ${
                        highlightedSessionId === tradeSession.id
                          ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-gray-900 transition-shadow'
                          : 'transition-shadow'
                      }`}>
                        {/* Top row: collapse/name/stats — actions */}
                        <div className="flex items-center justify-between px-3 py-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => toggleSessionCollapsed(tradeSession.id)}
                              className={`p-0.5 rounded transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                              title={isSessionCollapsed ? 'Expand group' : 'Collapse group'}
                            >
                              {isSessionCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                            </button>
                            {/* Color dot — click to open color picker */}
                            <div className="relative">
                              <button
                                onClick={() => setColorPickerSessionId(colorPickerSessionId === tradeSession.id ? null : tradeSession.id)}
                                className={`w-2.5 h-2.5 rounded-full ${sessionColor.dot} ring-1 ring-offset-1 ${
                                  darkMode ? 'ring-gray-600 ring-offset-gray-900' : 'ring-gray-300 ring-offset-white'
                                } hover:scale-110 transition-transform`}
                                title="Change session color"
                              />
                              {colorPickerSessionId === tradeSession.id && (
                                <div className={`absolute top-full left-0 mt-1 z-30 flex gap-1 p-1.5 rounded-lg shadow-lg ${
                                  darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                                }`}>
                                  {Object.entries(SESSION_COLORS).map(([key, c]) => (
                                    <button
                                      key={key}
                                      onClick={() => { updateSessionColor(tradeSession.id, key); setColorPickerSessionId(null); }}
                                      className={`w-4 h-4 rounded-full ${c.dot} ${
                                        tradeSession.color === key ? 'ring-2 ring-offset-1 ring-blue-400' : 'hover:scale-110'
                                      } transition-transform`}
                                      title={key}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            {renamingSession?.id === tradeSession.id ? (
                              <input
                                type="text"
                                autoFocus
                                value={renamingSession.name}
                                onChange={(e) => setRenamingSession({ id: tradeSession.id, name: e.target.value })}
                                onBlur={() => {
                                  renameSession(tradeSession.id, renamingSession.name);
                                  setRenamingSession(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    renameSession(tradeSession.id, renamingSession.name);
                                    setRenamingSession(null);
                                  } else if (e.key === 'Escape') {
                                    setRenamingSession(null);
                                  }
                                }}
                                className="input-modern text-sm font-semibold"
                              />
                            ) : (
                              <button
                                onClick={() => setRenamingSession({ id: tradeSession.id, name: tradeSession.name })}
                                className={`text-sm font-semibold ${sessionColor.text} hover:underline`}
                                title="Click to rename"
                              >
                                {tradeSession.name}
                              </button>
                            )}
                            {/* Compact stats row */}
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {sessionBannerData.count}T · {sessionBannerData.wins}W/{sessionBannerData.losses}L · {sessionBannerData.winRate.toFixed(0)}%
                            </span>
                            {sessionBannerData.avgR !== null && (
                              <span
                                className={`text-xs font-medium ${sessionBannerData.avgR >= 0 ? 'text-green-500' : 'text-red-500'}`}
                                title={`Total: ${sessionBannerData.totalR >= 0 ? '+' : ''}${sessionBannerData.totalR.toFixed(2)}R`}
                              >
                                {sessionBannerData.avgR >= 0 ? '+' : ''}{sessionBannerData.avgR.toFixed(2)}R avg
                              </span>
                            )}
                            {(sessionBannerData.longCount > 0 || sessionBannerData.shortCount > 0) && (
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {sessionBannerData.longCount > 0 && (
                                  <span className="text-green-500">{sessionBannerData.longCount}L</span>
                                )}
                                {sessionBannerData.longCount > 0 && sessionBannerData.shortCount > 0 && ' / '}
                                {sessionBannerData.shortCount > 0 && (
                                  <span className="text-red-500">{sessionBannerData.shortCount}S</span>
                                )}
                              </span>
                            )}
                            {sessionBannerData.timeSpan && (
                              <span className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <Clock size={10} />
                                {sessionBannerData.timeSpan}
                              </span>
                            )}
                            {(sessionBannerData.avgMAE !== null || sessionBannerData.avgMFE !== null) && (
                              <span
                                className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                title={sessionBannerData.mfeMaeRatio ? `MFE/MAE ratio: ${sessionBannerData.mfeMaeRatio.toFixed(2)}` : 'Average max adverse / favorable excursion'}
                              >
                                {sessionBannerData.avgMAE !== null && (
                                  <>MAE <span className="text-orange-400 font-medium">{sessionBannerData.avgMAE.toFixed(1)}</span></>
                                )}
                                {sessionBannerData.avgMAE !== null && sessionBannerData.avgMFE !== null && ' · '}
                                {sessionBannerData.avgMFE !== null && (
                                  <>MFE <span className="text-emerald-400 font-medium">{sessionBannerData.avgMFE.toFixed(1)}</span></>
                                )}
                              </span>
                            )}
                            {sessionBannerData.historical && (() => {
                              const h = sessionBannerData.historical;
                              const today = sessionBannerData.totalPnl;
                              const beating = today > h.avgPnl;
                              const tone = beating ? 'text-green-400' : today < h.avgPnl ? 'text-red-400' : 'text-gray-400';
                              return (
                                <span
                                  className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                  title={`Compared to ${h.sessionCount} prior session${h.sessionCount !== 1 ? 's' : ''} named "${tradeSession.name}"`}
                                >
                                  vs avg <span className={`font-medium ${tone}`}>
                                    {h.avgPnl >= 0 ? '+' : '-'}${fmtUSD(h.avgPnl)}
                                  </span>
                                  <span className="opacity-60"> (n={h.sessionCount})</span>
                                </span>
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Sparkline */}
                            {sessionBannerData.equityPoints.length >= 2 && (() => {
                              const pts = sessionBannerData.equityPoints;
                              const min = Math.min(0, ...pts);
                              const max = Math.max(0, ...pts);
                              const range = (max - min) || 1;
                              const w = 80, h = 22;
                              const stepX = pts.length > 1 ? w / (pts.length - 1) : w;
                              const yFor = (v) => h - ((v - min) / range) * h;
                              const d = pts.map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * stepX).toFixed(1)} ${yFor(v).toFixed(1)}`).join(' ');
                              const zeroY = yFor(0);
                              const stroke = sessionBannerData.totalPnl >= 0 ? '#22c55e' : '#ef4444';
                              return (
                                <svg width={w} height={h} className="opacity-90" title="Trade-by-trade running P&L">
                                  <line x1="0" y1={zeroY} x2={w} y2={zeroY} stroke={darkMode ? '#4b5563' : '#d1d5db'} strokeWidth="0.5" strokeDasharray="2,2" />
                                  <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                </svg>
                              );
                            })()}
                            <span className={`text-sm font-semibold ${sessionBannerData.totalPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {sessionBannerData.totalPnl >= 0 ? '+' : '-'}${fmtUSD(sessionBannerData.totalPnl)}
                            </span>
                            {/* Process rating: thumbs up / neutral / down (separate from W/L) */}
                            <div className={`flex items-center gap-0.5 px-1 py-0.5 rounded ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100/80'}`}>
                              <button
                                onClick={() => updateSessionRating(tradeSession.id, 'good')}
                                className={`text-xs leading-none px-1 py-0.5 rounded transition-all ${
                                  tradeSession.rating === 'good'
                                    ? 'bg-green-500/30 scale-110'
                                    : darkMode ? 'opacity-40 hover:opacity-100 hover:bg-green-500/20' : 'opacity-50 hover:opacity-100 hover:bg-green-100'
                                }`}
                                title="Good process"
                              >
                                👍
                              </button>
                              <button
                                onClick={() => updateSessionRating(tradeSession.id, 'neutral')}
                                className={`text-xs leading-none px-1 py-0.5 rounded transition-all ${
                                  tradeSession.rating === 'neutral'
                                    ? 'bg-gray-500/40 scale-110'
                                    : darkMode ? 'opacity-40 hover:opacity-100 hover:bg-gray-500/20' : 'opacity-50 hover:opacity-100 hover:bg-gray-200'
                                }`}
                                title="Neutral / mixed process"
                              >
                                ➖
                              </button>
                              <button
                                onClick={() => updateSessionRating(tradeSession.id, 'bad')}
                                className={`text-xs leading-none px-1 py-0.5 rounded transition-all ${
                                  tradeSession.rating === 'bad'
                                    ? 'bg-red-500/30 scale-110'
                                    : darkMode ? 'opacity-40 hover:opacity-100 hover:bg-red-500/20' : 'opacity-50 hover:opacity-100 hover:bg-red-100'
                                }`}
                                title="Bad process"
                              >
                                👎
                              </button>
                            </div>
                            {/* One-click AI insight */}
                            <button
                              onClick={() => generateSessionAiInsight(tradeSession)}
                              disabled={aiInsightLoading.has(tradeSession.id)}
                              className={`p-1 rounded transition-colors ${
                                aiInsightLoading.has(tradeSession.id)
                                  ? darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'
                                  : (tradeSession.aiInsight?.observations?.length > 0 || tradeSession.aiInsight?.text)
                                    ? darkMode ? 'text-purple-400 hover:bg-purple-500/20' : 'text-purple-500 hover:bg-purple-100'
                                    : darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-purple-300' : 'hover:bg-gray-200 text-gray-500 hover:text-purple-600'
                              }`}
                              title={
                                aiInsightLoading.has(tradeSession.id) ? 'Generating insight…'
                                : (tradeSession.aiInsight?.observations?.length > 0 || tradeSession.aiInsight?.text) ? 'Re-generate AI insight'
                                : 'Get AI insight on this session'
                              }
                            >
                              <Sparkles size={12} className={aiInsightLoading.has(tradeSession.id) ? 'animate-pulse' : ''} />
                            </button>
                            <button
                              onClick={() => {
                                // Reset filter state when toggling — fresh popover open
                                // shouldn't inherit search/filter from a previous session.
                                setBulkApplySearch('');
                                setBulkApplyFilter('all');
                                setBulkApplyExpanded(new Set());
                                setBulkApplyMenu(bulkApplyMenu?.sessionId === tradeSession.id ? null : { sessionId: tradeSession.id, tab: 'confluences' });
                              }}
                              className={`p-1 rounded transition-colors ${
                                bulkApplyMenu?.sessionId === tradeSession.id
                                  ? darkMode ? 'bg-gray-700 text-blue-400' : 'bg-gray-200 text-blue-600'
                                  : darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                              }`}
                              title="Bulk apply to all trades in group"
                            >
                              <Wand2 size={12} />
                            </button>
                            <button
                              onClick={() => { if (window.confirm(`Delete session "${tradeSession.name}"?`)) deleteSession(tradeSession.id); document.body.focus(); }}
                              className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-600 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                              title="Delete session"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        </div>

                        {/* Playbook picker — strategic intent label (optional).
                            Categorical layer on top of the free-form thesis — lets the
                            Coach Memory system aggregate sessions by intent across
                            months/years. Unassigned stays totally valid. */}
                        <div className={`px-3 pb-1.5 pt-1.5 ${darkMode ? 'border-t border-gray-700/30' : 'border-t border-gray-200/50'}`}>
                          {(() => {
                            const assigned = tradeSession.playbookId ? getPlaybookById(tradeSession.playbookId) : null;
                            const style = assigned ? getPlaybookColor(assigned) : null;
                            const isOpen = playbookPickerSessionId === tradeSession.id;
                            return (
                              <div className="relative flex items-center gap-1.5 flex-wrap">
                                <span className={`text-[10px] uppercase tracking-wider shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Playbook:</span>
                                {assigned ? (
                                  <button
                                    onClick={() => {
                                      setPlaybookPickerSessionId(isOpen ? null : tradeSession.id);
                                      setNewPlaybookDraft('');
                                    }}
                                    className={`group inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border transition-all ${style.bg} ${darkMode ? 'border-gray-700 hover:border-gray-500' : 'border-gray-300 hover:border-gray-500'}`}
                                    title="Click to change or remove the playbook"
                                  >
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} />
                                    <span className={darkMode ? 'text-gray-100' : 'text-gray-800'}>{assigned.name}</span>
                                    <ChevronDown size={10} className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} opacity-60 group-hover:opacity-100`} />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setPlaybookPickerSessionId(isOpen ? null : tradeSession.id);
                                      setNewPlaybookDraft('');
                                    }}
                                    className={`text-xs italic transition-colors ${darkMode ? 'text-gray-500 hover:text-purple-300' : 'text-gray-400 hover:text-purple-600'}`}
                                    title="Assign a playbook to enable cross-session pattern tracking"
                                  >
                                    + assign playbook (strategy this session ran)
                                  </button>
                                )}

                                {/* Picker popover */}
                                {isOpen && (
                                  <div
                                    className={`absolute top-full left-0 mt-1 z-30 w-64 rounded-lg shadow-lg border overflow-hidden ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className={`px-3 py-2 border-b text-[10px] uppercase tracking-wider ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                                      Select a playbook
                                    </div>
                                    <div className="max-h-56 overflow-y-auto py-1">
                                      {(playbooks || []).length === 0 ? (
                                        <div className={`px-3 py-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                          No playbooks yet. Type a name below to create your first one.
                                        </div>
                                      ) : (
                                        (playbooks || []).map(pb => {
                                          const pbStyle = getPlaybookColor(pb);
                                          const isCurrent = tradeSession.playbookId === pb.id;
                                          return (
                                            <button
                                              key={pb.id}
                                              onClick={() => {
                                                assignPlaybookToSession(tradeSession.id, pb.id);
                                                setPlaybookPickerSessionId(null);
                                              }}
                                              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${isCurrent ? (darkMode ? 'bg-gray-800' : 'bg-gray-100') : (darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100')}`}
                                            >
                                              <span className={`inline-block w-2 h-2 rounded-full ${pbStyle.dot}`} />
                                              <span className={`flex-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{pb.name}</span>
                                              {isCurrent && (
                                                <Check size={11} className={darkMode ? 'text-green-400' : 'text-green-600'} />
                                              )}
                                            </button>
                                          );
                                        })
                                      )}
                                    </div>
                                    {assigned && (
                                      <button
                                        onClick={() => {
                                          assignPlaybookToSession(tradeSession.id, null);
                                          setPlaybookPickerSessionId(null);
                                        }}
                                        className={`w-full px-3 py-1.5 text-left text-xs border-t transition-colors ${darkMode ? 'border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-red-400' : 'border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-red-600'}`}
                                      >
                                        Remove playbook from this session
                                      </button>
                                    )}
                                    <div className={`px-3 py-2 border-t ${darkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                                      <div className={`text-[10px] uppercase tracking-wider mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Create new</div>
                                      <div className="flex items-center gap-1">
                                        <input
                                          type="text"
                                          autoFocus
                                          value={newPlaybookDraft}
                                          onChange={(e) => setNewPlaybookDraft(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              const pb = createPlaybook(newPlaybookDraft);
                                              if (pb) {
                                                assignPlaybookToSession(tradeSession.id, pb.id);
                                                setPlaybookPickerSessionId(null);
                                                setNewPlaybookDraft('');
                                              }
                                            } else if (e.key === 'Escape') {
                                              setPlaybookPickerSessionId(null);
                                              setNewPlaybookDraft('');
                                            }
                                          }}
                                          placeholder="e.g. Rejection at Psych Level"
                                          className="flex-1 input-modern text-xs"
                                        />
                                        <button
                                          onClick={() => {
                                            const pb = createPlaybook(newPlaybookDraft);
                                            if (pb) {
                                              assignPlaybookToSession(tradeSession.id, pb.id);
                                              setPlaybookPickerSessionId(null);
                                              setNewPlaybookDraft('');
                                            }
                                          }}
                                          disabled={!newPlaybookDraft.trim()}
                                          className={`shrink-0 px-2 py-1 text-xs rounded transition-colors ${newPlaybookDraft.trim()
                                            ? (darkMode ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30' : 'bg-purple-100 text-purple-700 hover:bg-purple-200')
                                            : (darkMode ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')}`}
                                          title="Create playbook + assign to this session"
                                        >
                                          <Plus size={11} />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Inline thesis editor — session-level commentary */}
                        <div className="px-3 pb-2 pt-0">
                          {editingThesis?.id === tradeSession.id ? (
                            <div className="flex items-center gap-1.5">
                              <span className={`text-[10px] uppercase tracking-wider shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Thesis:</span>
                              <input
                                type="text"
                                autoFocus
                                value={editingThesis.thesis}
                                onChange={(e) => setEditingThesis({ id: tradeSession.id, thesis: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateSessionThesis(tradeSession.id, editingThesis.thesis.trim());
                                    setEditingThesis(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingThesis(null);
                                  }
                                }}
                                onBlur={() => {
                                  updateSessionThesis(tradeSession.id, editingThesis.thesis.trim());
                                  setEditingThesis(null);
                                }}
                                placeholder="Why did you take this series of trades?"
                                className="flex-1 input-modern text-xs"
                              />
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingThesis({ id: tradeSession.id, thesis: tradeSession.thesis || '' })}
                              className={`text-xs italic w-full text-left truncate transition-colors ${
                                tradeSession.thesis
                                  ? darkMode ? 'text-gray-300 hover:text-blue-300' : 'text-gray-600 hover:text-blue-600'
                                  : darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title={tradeSession.thesis ? 'Click to edit thesis' : 'Click to add a thesis for this group'}
                            >
                              {tradeSession.thesis
                                ? <><span className={`not-italic text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'} mr-1.5`}>Thesis:</span>{tradeSession.thesis}</>
                                : '+ add thesis / why did you take this series of trades?'}
                            </button>
                          )}
                        </div>

                        {/* Inline lessons editor — post-trade reflection */}
                        <div className="px-3 pb-2 pt-0">
                          {editingLessons?.id === tradeSession.id ? (
                            <div className="flex items-center gap-1.5">
                              <span className={`text-[10px] uppercase tracking-wider shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Lessons:</span>
                              <input
                                type="text"
                                autoFocus
                                value={editingLessons.lessons}
                                onChange={(e) => setEditingLessons({ id: tradeSession.id, lessons: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateSessionLessons(tradeSession.id, editingLessons.lessons.trim());
                                    setEditingLessons(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingLessons(null);
                                  }
                                }}
                                onBlur={() => {
                                  updateSessionLessons(tradeSession.id, editingLessons.lessons.trim());
                                  setEditingLessons(null);
                                }}
                                placeholder="What did you learn? What would you do differently?"
                                className="flex-1 input-modern text-xs"
                              />
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingLessons({ id: tradeSession.id, lessons: tradeSession.lessons || '' })}
                              className={`text-xs italic w-full text-left truncate transition-colors ${
                                tradeSession.lessons
                                  ? darkMode ? 'text-amber-200 hover:text-amber-300' : 'text-amber-700 hover:text-amber-600'
                                  : darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title={tradeSession.lessons ? 'Click to edit lessons' : 'Click to add post-trade lessons'}
                            >
                              {tradeSession.lessons
                                ? <><span className={`not-italic text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'} mr-1.5`}>Lessons:</span>{tradeSession.lessons}</>
                                : '+ add lessons / what did you learn from this group?'}
                            </button>
                          )}
                        </div>

                        {/* AI insight panel — severity-coded observation cards (coach engine).
                            Falls back to legacy single-string rendering for older persisted insights. */}
                        {(() => {
                          const insight = tradeSession.aiInsight;
                          if (!insight) return null;
                          // Filter out user-muted observations; enrich the
                          // survivors with a Mute action so the trader can
                          // silence ones they've already addressed.
                          const rawObservations = Array.isArray(insight.observations) ? insight.observations : [];
                          const observations = enrichWithMuteAction(filterMuted(rawObservations, mutedObservations));
                          const hasRich = observations.length > 0;
                          const hasLegacy = !hasRich && typeof insight.text === 'string' && insight.text.trim().length > 0;
                          if (!hasRich && !hasLegacy) return null;

                          // Historical sessions (prior trading days) are frozen artifacts:
                          // observations stay preserved exactly as generated, no auto-refresh,
                          // and the UI locks to a "Saved" pill with no pause/resume controls.
                          // Either the explicit flag set by the watcher OR a date check works.
                          const isHistorical =
                            insight.pauseReason === 'historical' ||
                            (tradeSession.date && currentTradingDay && tradeSession.date !== currentTradingDay);

                          return (
                            <div className="px-3 pb-2 pt-0 space-y-1.5">
                              {/* Header row — Sparkles + Live/Paused/Saved status + controls.
                                  Live   → manual Pause + Dismiss buttons
                                  Paused → manual Resume + Dismiss buttons
                                  Saved  → locked to memory, no play/X (prior-day session) */}
                              <div className={`flex items-center justify-between text-[10px] uppercase tracking-wider ${darkMode ? 'text-purple-400' : 'text-purple-500'}`}>
                                <div className="flex items-center gap-1.5">
                                  <Sparkles size={11} className={darkMode ? 'text-purple-400' : 'text-purple-500'} />
                                  <span>AI Coach</span>
                                  {insight.autoTrack ? (
                                    <span
                                      className="flex items-center gap-1"
                                      title="Live — auto-updating as trades change"
                                    >
                                      <span className="relative flex h-1.5 w-1.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                                      </span>
                                      <span className={darkMode ? 'text-green-400' : 'text-green-600'}>Live</span>
                                    </span>
                                  ) : isHistorical ? (
                                    <span
                                      className="flex items-center gap-1"
                                      title="Saved — locked snapshot from a prior trading day"
                                    >
                                      <span className={`inline-flex rounded-full h-1.5 w-1.5 ${darkMode ? 'bg-blue-400' : 'bg-blue-500'}`} />
                                      <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>Saved</span>
                                    </span>
                                  ) : (
                                    <span
                                      className="flex items-center gap-1"
                                      title="Paused — coaching persists, but won't auto-refresh until you resume"
                                    >
                                      <span className={`inline-flex rounded-full h-1.5 w-1.5 ${darkMode ? 'bg-gray-500' : 'bg-gray-400'}`} />
                                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Paused</span>
                                    </span>
                                  )}
                                  {hasRich && (
                                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] normal-case tracking-normal ${darkMode ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
                                      {observations.length} observation{observations.length !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>
                                {!isHistorical && (
                                  <div className="flex items-center gap-0.5 shrink-0">
                                    {insight.autoTrack ? (
                                      <button
                                        onClick={() => pauseSessionAiCoaching(tradeSession.id)}
                                        className={`p-0.5 rounded transition-colors ${darkMode ? 'hover:bg-purple-500/20 text-purple-400' : 'hover:bg-purple-100 text-purple-500'}`}
                                        title="Pause live coaching (keeps insight visible)"
                                      >
                                        <Pause size={10} />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => resumeSessionAiCoaching(tradeSession)}
                                        className={`p-0.5 rounded transition-colors ${darkMode ? 'hover:bg-green-500/20 text-green-400' : 'hover:bg-green-100 text-green-600'}`}
                                        title="Resume live coaching + refresh now"
                                      >
                                        <Play size={10} />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => setSessionAiInsight(tradeSession.id, null)}
                                      className={`p-0.5 rounded transition-colors ${darkMode ? 'hover:bg-red-500/20 hover:text-red-400 text-purple-400' : 'hover:bg-red-100 hover:text-red-600 text-purple-500'}`}
                                      title="Dismiss insight (removes the coaching — click Sparkle to regenerate)"
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Rich: severity-coded observation cards */}
                              {hasRich && observations.map((obs) => {
                                const style = SEVERITY_STYLE[obs.severity] || SEVERITY_STYLE.info;
                                return (
                                  <div
                                    key={obs.id}
                                    className={`text-xs px-2.5 py-2 rounded border-l-2 ${style.border} ${style.bg} ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${style.dot}`} />
                                      <div className="flex-1 min-w-0">
                                        {obs.title && (
                                          <div className={`font-semibold leading-snug ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                            {obs.title}
                                          </div>
                                        )}
                                        {obs.detail && (
                                          <div className={`mt-0.5 leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {obs.detail}
                                          </div>
                                        )}
                                        {obs.action && (
                                          <div className={`mt-1 italic leading-snug ${style.text}`}>
                                            → {obs.action}
                                          </div>
                                        )}
                                        {/* Structured one-click actions, when the observation
                                            ships them. The dispatcher in TradingJournal applies
                                            the change to journal state. */}
                                        {Array.isArray(obs.actions) && obs.actions.length > 0 && onCoachAction && (
                                          <ObservationActions
                                            observation={obs}
                                            onAction={onCoachAction}
                                            darkMode={darkMode}
                                            compact
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}

                              {/* Legacy fallback: single-string insight from pre-coach sessions */}
                              {hasLegacy && (
                                <div className={`flex items-start gap-2 text-xs px-2 py-1.5 rounded border-l-2 border-purple-400 ${
                                  darkMode ? 'bg-purple-500/10 text-purple-200' : 'bg-purple-50 text-purple-800'
                                }`}>
                                  <div className="flex-1">{insight.text}</div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Per-account breakdown chips */}
                        {sessionBannerData.accountBreakdown.length > 1 && (
                          <div className={`px-3 pb-2 flex flex-wrap items-center gap-1.5 ${darkMode ? 'border-t border-gray-700/40' : 'border-t border-gray-200/60'} pt-2`}>
                            <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Accounts:</span>
                            {sessionBannerData.accountBreakdown.map((a) => (
                              <span
                                key={a.accountId}
                                className={`text-[11px] px-1.5 py-0.5 rounded font-mono ${
                                  darkMode ? 'bg-gray-800/60 border border-gray-700' : 'bg-white/60 border border-gray-200'
                                }`}
                                title={`${a.count} trade${a.count !== 1 ? 's' : ''}`}
                              >
                                <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{hideAccounts ? '••••••' : a.name}</span>
                                <span className={`ml-1 font-semibold ${a.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {a.pnl >= 0 ? '+' : '-'}${fmtUSD(a.pnl)}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Confluence consensus chips — DNA of the session */}
                        {sessionBannerData.confluenceConsensus.length > 0 && sessionBannerData.count >= 2 && (
                          <div className={`px-3 pb-2 flex flex-wrap items-center gap-1.5 ${darkMode ? 'border-t border-gray-700/40' : 'border-t border-gray-200/60'} pt-2`}>
                            <span
                              className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                              title="Confluences across the group. Solid = present in ALL trades. Faded = present in some."
                            >
                              Consensus:
                            </span>
                            {sessionBannerData.confluenceConsensus.slice(0, 8).map((c) => (
                              <span
                                key={c.id}
                                className={`text-[11px] px-1.5 py-0.5 rounded border ${
                                  c.isUniversal
                                    ? darkMode
                                      ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-200 font-medium'
                                      : 'bg-cyan-100 border-cyan-300 text-cyan-800 font-medium'
                                    : darkMode
                                      ? 'bg-cyan-500/5 border-cyan-700/30 text-cyan-400/70'
                                      : 'bg-cyan-50 border-cyan-200/60 text-cyan-600/80'
                                }`}
                                title={c.isUniversal ? `In all ${c.total} trades — core thesis` : `In ${c.count} of ${c.total} trades`}
                              >
                                {c.name}
                                {!c.isUniversal && (
                                  <span className={`ml-1 text-[10px] ${darkMode ? 'text-cyan-500/60' : 'text-cyan-500/70'}`}>
                                    {c.count}/{c.total}
                                  </span>
                                )}
                              </span>
                            ))}
                            {sessionBannerData.confluenceConsensus.length > 8 && (
                              <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                +{sessionBannerData.confluenceConsensus.length - 8} more
                              </span>
                            )}
                          </div>
                        )}

                        {/* Bulk Apply Popover */}
                        {bulkApplyMenu?.sessionId === tradeSession.id && (() => {
                          const sessionTradeList = dayTrades.filter(t => t.sessionId === tradeSession.id);
                          const allHave = (field, value) => sessionTradeList.length > 0 &&
                            sessionTradeList.every(t => Array.isArray(t[field]) && t[field].includes(value));
                          const someHave = (field, value) => sessionTradeList.some(t => Array.isArray(t[field]) && t[field].includes(value));
                          const tabs = [
                            // v7.5.9 — include `category` so the Confluences tab
                            // can group by section. Tags/Mistakes are flat (no category).
                            // v7.6.x — Counter-Confluences mirrors Confluences but
                            // writes to the `counterConfluences` field. `mutexField`
                            // tells bulkApplyToSession to strip the same id from the
                            // `confluences` array when added, so a chip can't be
                            // both a positive AND counter on the same trade — same
                            // mutex the Edit Trade Modal enforces per-trade.
                            // Counter is intentionally NOT the default-active tab
                            // — it's "collapsed unless you open it" via the tab UI.
                            { id: 'confluences',        label: 'Confluences',   items: allConfluences.map(c => ({ id: c.id, label: c.name, category: c.category || 'Other' })), field: 'confluences' },
                            { id: 'counterConfluences', label: 'Counter (✗)',   items: allConfluences.map(c => ({ id: c.id, label: c.name, category: c.category || 'Other' })), field: 'counterConfluences', mutexField: 'confluences' },
                            { id: 'tags',               label: 'Tags',          items: availableEventTags.map(t => ({ id: t, label: t, category: null })),                     field: 'tags' },
                            { id: 'mistakes',           label: 'Mistakes',      items: mistakes.map(m => ({ id: m, label: m, category: null })),                                field: 'mistakes' }
                          ];
                          const activeTab = tabs.find(t => t.id === bulkApplyMenu.tab) || tabs[0];

                          // ── Filter + group items (v7.5.9 navigation upgrade) ──
                          // Apply: status filter → text search → category grouping.
                          // Order matters: status first so we don't iterate over
                          // chips we'd drop anyway, then text, then group.
                          const searchLower = bulkApplySearch.trim().toLowerCase();
                          const matchesStatus = (item) => {
                            const on = allHave(activeTab.field, item.id);
                            const some = !on && someHave(activeTab.field, item.id);
                            if (bulkApplyFilter === 'consensus') return on;
                            if (bulkApplyFilter === 'used') return on || some;
                            if (bulkApplyFilter === 'unused') return !on && !some;
                            return true; // 'all'
                          };
                          const matchesSearch = (item) => {
                            if (!searchLower) return true;
                            return item.label.toLowerCase().includes(searchLower);
                          };
                          const filteredItems = activeTab.items.filter(item => matchesStatus(item) && matchesSearch(item));

                          // Group by category — only meaningful for the two
                          // confluence variants (positive + counter). Tags/Mistakes
                          // get a single null-key bucket which renders without a
                          // section header.
                          const isCategorized = activeTab.id === 'confluences' || activeTab.id === 'counterConfluences';
                          const grouped = new Map();
                          for (const item of filteredItems) {
                            const cat = isCategorized ? item.category : null;
                            if (!grouped.has(cat)) grouped.set(cat, []);
                            grouped.get(cat).push(item);
                          }
                          // Active search auto-expands all categories so matches
                          // are visible without the user clicking around.
                          const searchActive = searchLower.length > 0;
                          // Used-count summary for the category header — quick
                          // glance at "this category has 2/5 chips tagged on the group."
                          const categoryUsedCount = (catItems) => {
                            let n = 0;
                            for (const item of catItems) {
                              if (someHave(activeTab.field, item.id) || allHave(activeTab.field, item.id)) n++;
                            }
                            return n;
                          };
                          return (
                            <div className={`px-3 pb-2 pt-2 ${darkMode ? 'border-t border-gray-700/40' : 'border-t border-gray-200/60'}`}>
                              <div className="flex items-center gap-1 mb-2">
                                {tabs.map(t => (
                                  <button
                                    key={t.id}
                                    onClick={() => {
                                      // Switching tabs resets search/filter so the user
                                      // doesn't carry a stale "vwap" search from
                                      // Confluences into Tags and see nothing.
                                      setBulkApplySearch('');
                                      setBulkApplyFilter('all');
                                      setBulkApplyMenu({ sessionId: tradeSession.id, tab: t.id });
                                    }}
                                    className={`text-[11px] px-2 py-0.5 rounded transition-colors ${
                                      activeTab.id === t.id
                                        ? darkMode ? 'bg-blue-500/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                                        : darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                                    }`}
                                  >
                                    {t.label}
                                  </button>
                                ))}
                                <span className={`ml-auto text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Click chip to toggle on all {sessionTradeList.length} trades
                                </span>
                              </div>

                              {/* Search + status filter row (v7.5.9 navigation upgrade) */}
                              <div className="flex items-center gap-2 mb-2">
                                <div className="relative flex-1 min-w-0">
                                  <Search
                                    size={11}
                                    className={`absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${
                                      darkMode ? 'text-gray-500' : 'text-gray-400'
                                    }`}
                                  />
                                  <input
                                    type="text"
                                    value={bulkApplySearch}
                                    onChange={(e) => setBulkApplySearch(e.target.value)}
                                    placeholder={`Search ${activeTab.label.toLowerCase()}…`}
                                    className="w-full input-modern pl-6 text-xs"
                                    style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
                                  />
                                </div>
                                {/* Status filter pills — let the user narrow to chips
                                    that are tagged on all/some/none of the trades. */}
                                {[
                                  { id: 'all',       label: 'All' },
                                  { id: 'used',      label: 'Used' },
                                  { id: 'consensus', label: 'Consensus' },
                                  { id: 'unused',    label: 'Unused' },
                                ].map(f => (
                                  <button
                                    key={f.id}
                                    onClick={() => setBulkApplyFilter(f.id)}
                                    className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors shrink-0 ${
                                      bulkApplyFilter === f.id
                                        ? darkMode ? 'bg-blue-500/30 border-blue-500/50 text-blue-200' : 'bg-blue-100 border-blue-300 text-blue-800'
                                        : darkMode ? 'bg-transparent border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500' : 'bg-transparent border-gray-300 text-gray-500 hover:text-gray-800 hover:border-gray-400'
                                    }`}
                                    title={
                                      f.id === 'all' ? 'Show every chip' :
                                      f.id === 'used' ? 'Only chips tagged on at least one trade in this group' :
                                      f.id === 'consensus' ? 'Only chips tagged on EVERY trade in this group' :
                                      'Only chips not yet tagged on any trade'
                                    }
                                  >
                                    {f.label}
                                  </button>
                                ))}
                              </div>

                              {/* Chip grid — sectioned by category for Confluences,
                                  flat for Tags/Mistakes. Sections are collapsible
                                  (click header to toggle). When a search is active,
                                  all sections render expanded regardless of the
                                  collapsed-set state so matches stay visible. */}
                              <div className="max-h-80 overflow-y-auto">
                                {filteredItems.length === 0 ? (
                                  <p className={`text-xs italic px-1 py-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {activeTab.items.length === 0
                                      ? `No ${activeTab.label.toLowerCase()} available`
                                      : `No ${activeTab.label.toLowerCase()} match this search / filter`}
                                  </p>
                                ) : (
                                  Array.from(grouped.entries()).map(([cat, catItems]) => {
                                    // Default = collapsed. Search auto-expands all
                                    // sections so matches stay visible. Otherwise
                                    // user must click a header to expand it.
                                    const isExpanded = searchActive || bulkApplyExpanded.has(cat);
                                    const usedCount = isCategorized ? categoryUsedCount(catItems) : 0;
                                    return (
                                      <div key={cat || '__flat__'} className="mb-0.5 last:mb-0">
                                        {/* Category header — only shown for Confluences (cat !== null).
                                            Tags / Mistakes render their flat list with no header. */}
                                        {cat && (
                                          <button
                                            type="button"
                                            onClick={() => setBulkApplyExpanded(prev => {
                                              const next = new Set(prev);
                                              next.has(cat) ? next.delete(cat) : next.add(cat);
                                              return next;
                                            })}
                                            className={`w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-1 py-0.5 rounded transition-colors ${
                                              darkMode ? 'text-gray-400 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-100'
                                            }`}
                                            title={isExpanded ? `Collapse ${cat}` : `Expand ${cat}`}
                                          >
                                            <ChevronRight
                                              size={10}
                                              className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                            />
                                            <span className="font-semibold">{cat}</span>
                                            <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>
                                              · {catItems.length}
                                            </span>
                                            {usedCount > 0 && (
                                              <span className={`ml-auto ${darkMode ? 'text-blue-400/80' : 'text-blue-600/80'}`}>
                                                {usedCount} tagged
                                              </span>
                                            )}
                                          </button>
                                        )}
                                        {isExpanded && (
                                          <div className="flex flex-wrap gap-1 mt-0.5 mb-1 px-1">
                                            {catItems.map(item => {
                                              const on = allHave(activeTab.field, item.id);
                                              const partial = !on && someHave(activeTab.field, item.id);
                                              // Counter-Confluences tab gets orange theming (matches the
                                              // ✗ chips rendered on trade cards) and the ✗ prefix so the
                                              // user can tell at a glance that they're tagging AGAINST.
                                              const isCounter = activeTab.id === 'counterConfluences';
                                              return (
                                                <button
                                                  key={item.id}
                                                  onClick={() => bulkApplyToSession(tradeSession.id, activeTab.field, item.id, activeTab.mutexField || null)}
                                                  className={`text-[11px] px-1.5 py-0.5 rounded border transition-colors ${
                                                    on
                                                      ? isCounter
                                                        ? darkMode ? 'bg-orange-500/30 border-orange-500/50 text-orange-200' : 'bg-orange-100 border-orange-300 text-orange-800'
                                                        : darkMode ? 'bg-blue-500/30 border-blue-500/50 text-blue-200' : 'bg-blue-100 border-blue-300 text-blue-800'
                                                      : partial
                                                      ? isCounter
                                                        ? darkMode ? 'bg-orange-500/10 border-orange-500/30 text-orange-300/80 border-dashed' : 'bg-orange-50 border-orange-200 text-orange-600 border-dashed'
                                                        : darkMode ? 'bg-blue-500/10 border-blue-500/30 text-blue-300/80 border-dashed' : 'bg-blue-50 border-blue-200 text-blue-600 border-dashed'
                                                      : isCounter
                                                        ? darkMode ? 'bg-gray-800/60 border-gray-700 text-gray-300 hover:border-orange-500/50' : 'bg-white border-gray-200 text-gray-700 hover:border-orange-400'
                                                        : darkMode ? 'bg-gray-800/60 border-gray-700 text-gray-300 hover:border-blue-500/50' : 'bg-white border-gray-200 text-gray-700 hover:border-blue-400'
                                                  }`}
                                                  title={on ? 'On all trades — click to remove' : partial ? 'On some trades — click to add to all' : 'Click to add to all'}
                                                >
                                                  {on && <Check size={9} className="inline mr-0.5" strokeWidth={3} />}
                                                  {isCounter ? `✗ ${item.label}` : item.label}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Trade Card — hidden when session is collapsed */}
                    {!isSessionCollapsed && (
                    <div
                    id={`trade-card-${trade.id}`}
                    className={`p-3 rounded-lg border transition-colors relative ${
                      tradeSession ? `border-l-4 ${sessionColor.border} ` : ''
                    }${
                      confluenceCopySource?.id === trade.id
                        ? darkMode ? 'bg-cyan-900/20 border-cyan-500/50 ring-1 ring-cyan-500/30' : 'bg-cyan-50 border-cyan-300 ring-1 ring-cyan-400/40'
                        : trade.isOpen
                        ? darkMode
                          ? 'bg-yellow-900/20 border-yellow-600/50 ring-1 ring-yellow-500/30'
                          : 'bg-yellow-50 border-yellow-300 ring-1 ring-yellow-400/40'
                        : darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {/* Session Group Mode Checkbox */}
                    {sessionGroupMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSessionGroupSelected(prev => {
                            const next = new Set(prev);
                            next.has(trade.id) ? next.delete(trade.id) : next.add(trade.id);
                            return next;
                          });
                        }}
                        className={`absolute -top-1.5 -left-1.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer shadow-xs z-10 ${
                          sessionGroupSelected.has(trade.id)
                            ? 'bg-blue-500 border-blue-500'
                            : darkMode ? 'bg-gray-800 border-gray-500 hover:border-blue-400' : 'bg-white border-gray-400 hover:border-blue-500'
                        }`}
                      >
                        {sessionGroupSelected.has(trade.id) && (
                          <Check size={10} className="text-white" strokeWidth={3} />
                        )}
                      </button>
                    )}

                    {/* Journaled Indicator / Copy Mode Checkbox (hidden during session group mode) */}
                    {!sessionGroupMode && confluenceCopySource ? (
                      // COPY MODE: show checkbox (or source badge for the source trade)
                      confluenceCopySource.id === trade.id ? (
                        <div className="absolute -top-1.5 -left-1.5 px-1.5 py-0.5 bg-cyan-500 rounded-full flex items-center justify-center shadow-xs">
                          <span className="text-white text-[7px] font-bold leading-none">SRC</span>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleConfluenceCopyTrade(trade.id); }}
                          className={`absolute -top-1.5 -left-1.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer shadow-xs ${
                            confluenceCopySelected.has(trade.id)
                              ? 'bg-cyan-500 border-cyan-500'
                              : darkMode ? 'bg-gray-800 border-gray-500 hover:border-cyan-400' : 'bg-white border-gray-400 hover:border-cyan-500'
                          }`}
                        >
                          {confluenceCopySelected.has(trade.id) && (
                            <Check size={10} className="text-white" strokeWidth={3} />
                          )}
                        </button>
                      )
                    ) : (
                      // NORMAL MODE: green journaled circle
                      !trade.isOpen && isJournaled && (
                        <div className="absolute -top-1 -left-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center shadow-xs">
                          <span className="text-white text-[8px] leading-none">✓</span>
                        </div>
                      )
                    )}

                    {/* LIVE Badge (open trades) — hidden during confluence copy mode */}
                    {trade.isOpen && !confluenceCopySource && (
                      <div className="absolute -top-2 -left-2 flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500 text-black text-[10px] font-bold rounded-full shadow-xs uppercase tracking-wider">
                        <Activity size={10} className="animate-pulse" />
                        LIVE
                      </div>
                    )}

                    {/* Line 1: # Symbol Direction Time Qty Entry SL Exit | P&L R-Multiple Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          #{index + 1}
                        </span>
                        <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {trade.symbol}
                          {trade.dayBoundaryClosed && (
                            <AlertTriangle
                              className="w-3.5 h-3.5 text-orange-500 inline ml-1"
                              title="Position held past 5pm ET - force closed at day boundary"
                            />
                          )}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          trade.direction?.toLowerCase() === 'long'
                            ? 'bg-green-500/20 text-green-500'
                            : 'bg-red-500/20 text-red-500'
                        }`}>
                          {trade.direction || 'N/A'}
                        </span>
                        {(trade.entryTime || trade.exitTime) && (
                          <span className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                            <Clock size={12} />
                            {trade.entryTime && <span>{trade.entryTime}</span>}
                            {trade.entryTime && trade.exitTime && <span>→</span>}
                            {trade.exitTime && <span>{trade.exitTime}</span>}
                          </span>
                        )}
                        <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                          Qty: {trade.quantity || 1}
                        </span>
                        <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                          Entry: {trade.entry || 'N/A'}
                        </span>
                        {(trade.stopLoss || trade.stopLossPoints) && (
                          <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                            SL: {trade.stopLoss || `${trade.stopLossPoints} pts`}
                          </span>
                        )}
                        {!trade.isOpen && (
                          <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                            Exit: {trade.exit ? Number(trade.exit).toFixed(2) : 'N/A'}
                          </span>
                        )}
                        {trade.isOpen && (
                          <span className="text-sm text-yellow-500 font-medium italic">
                            In Position
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {trade.isOpen ? (
                          /* Open trade: no P&L yet */
                          <span className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            P&L pending...
                          </span>
                        ) : (
                          /* Closed trade: show P&L and R-Multiple */
                          <>
                            <span className={`text-base ${darkMode ? 'text-gray-200' : 'text-gray-500'}`}>Net P&L:</span>
                            <span className={`text-lg font-semibold ${netPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {netPnl >= 0 ? '+' : '-'}${fmtUSD(netPnl)}
                            </span>
                            {rMultiple !== 0 && (
                              <>
                                <span className={`text-base ${darkMode ? 'text-gray-200' : 'text-gray-500'}`}>R-Multiple:</span>
                                <span className={`text-lg font-medium ${rMultiple >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {rMultiple >= 0 ? '+' : '-'}{Math.abs(rMultiple).toFixed(2)}R
                                </span>
                              </>
                            )}
                          </>
                        )}
                        {/* Copy confluences button — only when trade has confluences & 2+ trades exist */}
                        {!confluenceCopySource && ((trade.confluences && trade.confluences.length > 0) || (trade.counterConfluences && trade.counterConfluences.length > 0)) && dayTrades.length > 1 && (
                          <button
                            onClick={() => enterConfluenceCopyMode(trade, dayTrades)}
                            className={`p-1.5 rounded hover:bg-cyan-500 hover:text-white transition-colors ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                            title="Copy confluences to other trades today"
                          >
                            <Copy size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => onEditTrade(trade)}
                          className={`p-1.5 rounded hover:bg-blue-500 hover:text-white transition-colors ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                          title={trade.isOpen ? "Journal This Trade" : "Edit Trade"}
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => onDeleteTrade(trade.id)}
                          className={`p-1.5 rounded hover:bg-red-500 hover:text-white transition-colors ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                          title="Delete Trade"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Line 2: Setup | ATM | Experiment | Account (right-aligned).
                        v7.6.x — Setup is now its own emerald pill with a
                        leading "Setup" label, distinct from the cyan
                        confluence chips and purple ATM chip. Old "plain text
                        setup name + ATM pill" layout made the setup visually
                        identical to a normal sentence, which let the eye
                        confuse setup names with confluence chips below. */}
                    <div className={`flex items-center justify-between gap-4 mt-2 text-sm`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 font-medium ${
                            trade.setup
                              ? (darkMode
                                  ? 'bg-emerald-900/50 text-emerald-200 border border-emerald-700/40'
                                  : 'bg-emerald-100 text-emerald-800 border border-emerald-200')
                              : (darkMode
                                  ? 'bg-gray-700/50 text-gray-400 border border-gray-600/40'
                                  : 'bg-gray-100 text-gray-500 border border-gray-200')
                          }`}
                          title={trade.setup ? `Setup: ${trade.setup}` : 'No setup tagged'}
                        >
                          <span className={`uppercase tracking-wide text-[10px] ${
                            darkMode ? 'text-emerald-400/70' : 'text-emerald-700/70'
                          } ${!trade.setup ? (darkMode ? 'text-gray-500' : 'text-gray-400') : ''}`}>
                            Setup
                          </span>
                          <span>{trade.setup || 'None'}</span>
                        </span>
                        {trade.atmStrategy && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {trade.atmStrategy}
                          </span>
                        )}
                        {/* Phase 16 — Experiment badge. Fuchsia distinguishes
                            from ATM (purple), confluences (cyan), tags (blue).
                            Coach's experimentComparison analyzer compares
                            trades sharing this label by R-multiple + WR. */}
                        {trade.experiment && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 ${
                              darkMode ? 'bg-fuchsia-900/50 text-fuchsia-200 border border-fuchsia-700/40' : 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200'
                            }`}
                            title={`Part of experiment: ${trade.experiment} — Coach is comparing variants`}
                          >
                            <span aria-hidden="true">🧪</span>
                            <span className="font-medium">EXP:</span>
                            {trade.experiment}
                          </span>
                        )}
                      </div>
                      {account && (
                        <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${
                          darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {hideAccounts ? '••••••' : (account.name || account.id)}
                        </span>
                      )}
                    </div>
                    {/* Line 3: Confluences (only if any).
                        v7.5.13 — hide chips whose display name equals the trade's
                        setup name (case-insensitive). Implied Confluences often
                        share the setup's name (e.g., setup "15M 05 EMA" pins
                        "15M 05 EMA" as implied), which produced a visual
                        duplicate on every trade card. The data is still stored
                        (Coach analyzers still see the chip), just not rendered
                        twice. */}
                    {trade.confluences && trade.confluences.length > 0 && (() => {
                      const setupKey = (trade.setup || '').trim().toLowerCase();
                      const visibleConfs = trade.confluences.filter(conf => {
                        const name = (getConfluenceName(conf) || '').trim().toLowerCase();
                        return name !== setupKey;
                      });
                      if (visibleConfs.length === 0) return null;
                      return (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {visibleConfs.map(conf => (
                            <span key={conf} className={`text-xs px-2 py-0.5 rounded ${
                              darkMode ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-800'
                            }`}>
                              {getConfluenceName(conf)}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    {/* Counter-Confluences (Against Trade) */}
                    {trade.counterConfluences && trade.counterConfluences.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {trade.counterConfluences.map(conf => (
                          <span key={`cc-${conf}`} className={`text-xs px-2 py-0.5 rounded ${
                            darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800'
                          }`}>
                            ✗ {getConfluenceName(conf)}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Line 4: Tags & Mistakes (only if any) */}
                    {((trade.tags && trade.tags.length > 0) || (trade.mistakes && trade.mistakes.length > 0)) && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {trade.tags?.map(tag => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded-sm bg-blue-500/20 text-blue-400">
                            {tag}
                          </span>
                        ))}
                        {trade.mistakes?.map(mistake => (
                          <span key={mistake} className="text-xs px-2 py-0.5 rounded-sm bg-red-500/20 text-red-400">
                            ⚠ {mistake}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Line 5: Coach trade-flag chips — derived from
                        trade.barAnalysis. Renders nothing if the trade hasn't
                        been bar-analyzed yet (Settings → Charts → Backfill is
                        the prerequisite). Hover the chip for explanation. */}
                    {trade.barAnalysis && (
                      <div className="mt-2">
                        <TradeFlagChips trade={trade} darkMode={darkMode} />
                      </div>
                    )}
                    {/* Phase 19 — In-trade counter-signal chips. Renders
                        warnings for signals that fired AGAINST the trade
                        between entry and exit (e.g., "15m close vs 9 EMA
                        @ +12m"). Hover for close/EMA/distance detail.
                        Hidden when no signals fired (or trade unanalyzed). */}
                    {Array.isArray(trade.barAnalysis?.counterSignals) && trade.barAnalysis.counterSignals.length > 0 && (
                      <div className="mt-2">
                        <CounterSignalChips trade={trade} darkMode={darkMode} />
                      </div>
                    )}
                    {/* Rendered notes — shown for CLOSED trades only. While a
                        trade is live the LiveTradePanel below owns notes
                        display via its textarea (the editable source of truth),
                        so rendering them here too would be a redundant duplicate.

                        v7.6.x — Auto-synced metadata ("Auto-synced from
                        NinjaTrader / ATM: X / Commission: Y") collapses to
                        a single ⓘ icon with the full text on hover. With
                        15-20 trades per card stack, those 1-3 system lines
                        on every card were dominating the visual weight
                        without carrying trader-actionable info. User-added
                        notes (anything after the auto prefix) render
                        normally, preserving any Quill HTML formatting. */}
                    {trade.notes && !trade.isOpen && (() => {
                      const { wasAutoSynced, autoPrefix, stripped } = stripAutoSyncedPrefix(trade.notes);
                      if (!wasAutoSynced) {
                        return (
                          <div
                            className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                            dangerouslySetInnerHTML={{ __html: trade.notes }}
                          />
                        );
                      }
                      return (
                        <div className="mt-2 flex items-start gap-2">
                          <span
                            className={`inline-flex items-center shrink-0 cursor-help select-none mt-0.5 ${
                              darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title={autoPrefix}
                            aria-label="Auto-synced trade metadata"
                          >
                            <Info size={14} />
                          </span>
                          {stripped && (
                            <div
                              className={`flex-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                              dangerouslySetInnerHTML={{ __html: stripped }}
                            />
                          )}
                        </div>
                      );
                    })()}
                    {/* Phase 3: Live journaling panel — only on open trades,
                        hidden during confluence-copy / session-group modes so
                        it doesn't fight the overlay UI for attention. */}
                    {trade.isOpen && !confluenceCopySource && !sessionGroupMode && (
                      <LiveTradePanel
                        trade={trade}
                        darkMode={darkMode}
                        setTrades={setTrades}
                        allTrades={allTrades || trades}
                        getConfluenceName={getConfluenceName}
                        setupDetails={setupDetails}
                        onEditTrade={onEditTrade}
                        todayTrades={dayTrades}
                        journalEntry={journalEntries?.[selectedDate] ? { ...journalEntries[selectedDate], date: selectedDate } : null}
                        allJournalEntries={journalEntries}
                        getTradeNetPnL={getTradeNetPnL}
                        getAccountById={getAccountById}
                        userTier={userTier}
                        todayEvents={journalEntries?.[selectedDate]?.newsEvents || []}
                        coachAlerts={coachAlerts}
                      />
                    )}
                  </div>
                    )}
                  </React.Fragment>
                );
              });
              })()}
            </div>
          )}

          {/* Daily Summary */}
          {dayTrades.length > 0 && (
            <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Total P&L</span>
                  <p className={`font-semibold ${dayStats.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {dayStats.pnl >= 0 ? '+' : '-'}${fmtUSD(dayStats.pnl)}
                  </p>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Win Rate</span>
                  <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {dayStats.winRate.toFixed(0)}% ({dayStats.wins}W / {dayStats.losses}L)
                  </p>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Avg R-Multiple</span>
                  <p className={`font-semibold ${dayStats.rMultiple >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {dayStats.rMultiple !== 0 ? `${dayStats.rMultiple >= 0 ? '+' : '-'}${Math.abs(dayStats.rMultiple).toFixed(2)}R` : '-'}
                  </p>
                </div>
                {dayStats.setups.length > 0 && (
                  <div>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Setups</span>
                    <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} truncate`} title={dayStats.setups.join(', ')}>
                      {dayStats.setups.slice(0, 2).join(', ')}{dayStats.setups.length > 2 ? ` +${dayStats.setups.length - 2}` : ''}
                    </p>
                  </div>
                )}
                {dayStats.mistakes.length > 0 && (
                  <div>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Mistakes</span>
                    <p className="font-semibold text-red-500">
                      {dayStats.mistakes.length} made
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JournalView;
